import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { createGlobalStyle, css, keyframes } from 'styled-components';
import { FiCheck, FiChevronDown, FiChevronLeft, FiChevronRight, FiChevronUp, FiEdit2, FiPaperclip, FiPlus, FiRefreshCw, FiRotateCcw, FiTrash2, FiX, FiCalendar, FiBarChart2, FiMaximize2, FiMinimize2 } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import KanbanTemplatesPanel from './KanbanTemplatesPanel';
import GodModeControl from '../GodModeControl';
import DurationPicker, { formatDurationLabel } from '../DurationPicker';
import { useApp } from '../../context/AppContext';

const AV_COLORS = ['#2ea3f2', '#7c5cf9', '#f2a91c', '#22c07a', '#ef4b6a', '#14b8a6', '#f97316'];
const COLS = [
  { k: 'new', api: 'new', t: 'Новое' },
  { k: 'work', api: 'in_progress', t: 'В работе' },
  { k: 'review', api: 'done', t: 'Готово → на проверку' },
];

const DELAY_REASONS = [
  { id: 'too_many_tasks', label: 'Много параллельных задач' },
  { id: 'waiting_external', label: 'Ждал ответ / данные' },
  { id: 'scope_grew', label: 'Задача оказалась шире' },
  { id: 'urgent_interrupt', label: 'Отвлекли срочным' },
  { id: 'forgot_timer', label: 'Поздно нажал «готово» / «в работу»' },
  { id: 'other', label: 'Другое' },
];

const TIMING_LABEL = {
  early: 'раньше срока',
  on_time: 'в срок',
  late: 'с опозданием',
  no_limit: 'без лимита',
};

const dropIn = keyframes`from{opacity:0;transform:translateY(-20px)}`;
const cardIn = keyframes`from{opacity:0;transform:translateY(12px) scale(.95)}`;
const jump = keyframes`30%{transform:translateY(-8px) scale(1.03)}`;
const shm = keyframes`40%,100%{left:130%}`;
const blobF = keyframes`to{transform:translate(40px,30px) scale(1.12)}`;
const gradM = keyframes`to{background-position:260% 0}`;
const pul = keyframes`50%{opacity:.3}`;
const tIn = keyframes`from{opacity:0;transform:translateY(16px) scale(.92)}`;

const Fonts = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@500;600;700;800&family=JetBrains+Mono:wght@700;800&family=Unbounded:wght@700&display=swap');
`;

const Overlay = styled.div`
  position: fixed !important;
  top: 0 !important;
  left: ${(p) => (p.$mobile || p.$full ? '0' : '320px')} !important;
  right: 0 !important;
  bottom: 0 !important;
  width: ${(p) => (p.$mobile || p.$full ? '100vw' : 'calc(100vw - 320px)')} !important;
  height: 100vh !important;
  z-index: 200001;
  display: flex;
  pointer-events: auto;
  @media (max-width: 900px) {
    left: 0 !important;
    width: 100vw !important;
  }
`;

const Shell = styled.div`
  --bg: ${(p) => (p.$light ? '#eef2fa' : '#070b14')};
  --panel: ${(p) => (p.$light ? 'rgba(255,255,255,.88)' : 'rgba(15,21,36,.62)')};
  --panel2: ${(p) => (p.$light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)')};
  --solid: ${(p) => (p.$light ? '#ffffff' : '#101828')};
  --txt: ${(p) => (p.$light ? '#44566e' : '#eaf2fb')};
  --mut: ${(p) => (p.$light ? '#6b7c93' : '#8b96ab')};
  --head: ${(p) => (p.$light ? '#556780' : '#eaf2fb')};
  --stroke: ${(p) => (p.$light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)')};
  --blue: #38b6ff;
  --amber: #fbbf24;
  --green: #2dd4a0;
  --red: #fb7185;
  --violet: #8b5cf6;
  --shadow: ${(p) => (p.$light ? '0 14px 40px rgba(23,35,59,.12)' : '0 14px 40px rgba(0,0,0,.45)')};
  --grad: linear-gradient(135deg,#38b6ff,#8b5cf6);
  --blob-op: ${(p) => (p.$light ? '.35' : '.5')};
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  font-family: 'Inter', system-ui, sans-serif;
  background: var(--bg);
  color: var(--txt);
  font-size: 14px;
  transition: background .4s, color .4s;
`;

const Blob = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: var(--blob-op);
  z-index: 0;
  pointer-events: none;
  animation: ${blobF} 16s ease-in-out infinite alternate;
  &.b1 { width: 520px; height: 520px; top: -160px; left: -120px; background: radial-gradient(circle,#1e6fa8,transparent 70%); }
  &.b2 { width: 620px; height: 620px; top: -220px; right: -160px; background: radial-gradient(circle,#5b3bb8,transparent 70%); animation-delay: -6s; }
  &.b3 { width: 420px; height: 420px; bottom: -160px; left: 35%; background: radial-gradient(circle,#0e7a5f,transparent 70%); animation-delay: -11s; }
`;

const GridOv = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background-image: ${(p) =>
    p.$light
      ? 'linear-gradient(rgba(23,35,59,.05) 1px,transparent 1px),linear-gradient(90deg,rgba(23,35,59,.05) 1px,transparent 1px)'
      : 'linear-gradient(rgba(255,255,255,.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.03) 1px,transparent 1px)'};
  background-size: 48px 48px;
  mask-image: radial-gradient(70% 60% at 50% 30%, #000, transparent);
`;

const BgCanvas = styled.canvas`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
`;

const Scroll = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  overflow: auto;
  padding: 0 0 24px;
`;

const glass = css`
  background: var(--panel);
  backdrop-filter: blur(20px) saturate(1.4);
  -webkit-backdrop-filter: blur(20px) saturate(1.4);
  border: 1px solid var(--stroke);
  border-radius: 20px;
  box-shadow: var(--shadow);
`;

const Topbar = styled.header`
  ${glass};
  position: sticky;
  top: 10px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin: 10px 16px 0;
  padding: 12px 16px;
  animation: ${dropIn} .6s cubic-bezier(.2,.9,.3,1.2) both;
  @media (max-width: 560px) {
    margin: 8px 10px 0;
    padding: 10px 12px;
    gap: 8px;
  }
`;

const TopbarEnd = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-left: auto;
  flex-shrink: 0;
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  b { font-family: 'Unbounded', sans-serif; font-size: 14.5px; letter-spacing: .3px; color: var(--head) !important; -webkit-text-fill-color: var(--head) !important; }
  span {
    display: block;
    font-size: 10px;
    color: var(--mut);
    font-weight: 700;
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }
  @media (max-width: 560px) { span { display: none; } }
`;

const BoardPick = styled.div`
  position: relative;
  min-width: 0;
`;

const BoardBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  border: none;
  padding: 2px 4px;
  margin: -2px -4px;
  border-radius: 10px;
  cursor: pointer;
  min-width: 0;
  text-align: left;
  transition: background .18s;
  &:hover { background: var(--panel2); }
  .txt { min-width: 0; }
  .txt > b, .txt > span { display: block; max-width: 240px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .chev { color: var(--mut); display: flex; transition: transform .2s; }
  &.open .chev { transform: rotate(180deg); }
`;

const BoardMenu = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  z-index: 80;
  min-width: 260px;
  padding: 8px;
  border-radius: 16px;
  background: var(--solid);
  border: 1px solid var(--stroke);
  box-shadow: var(--shadow);
  animation: ${tIn} .18s ease both;
  h5 {
    margin: 4px 8px 6px;
    font-size: 10px;
    letter-spacing: 1.4px;
    text-transform: uppercase;
    color: var(--mut);
    font-weight: 800;
  }
  hr { border: none; border-top: 1px solid var(--stroke); margin: 6px 4px; }
`;

const BoardItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 10px;
  border-radius: 11px;
  border: none;
  cursor: pointer;
  font-weight: 800;
  font-size: 12.5px;
  text-align: left;
  color: ${(p) => (p.$danger ? 'var(--red, #ef4444)' : 'var(--txt)')};
  background: ${(p) => (p.$active ? 'color-mix(in srgb, var(--blue) 16%, transparent)' : 'transparent')};
  &:hover { background: ${(p) => (p.$danger ? 'color-mix(in srgb, #ef4444 14%, transparent)' : 'var(--panel2)')}; }
  .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const BoardRow = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  border-radius: 11px;
  background: ${(p) => (p.$active ? 'color-mix(in srgb, var(--blue) 16%, transparent)' : 'transparent')};
  &:hover { background: var(--panel2); }
  ${BoardItem} {
    flex: 1;
    min-width: 0;
    background: transparent !important;
    &:hover { background: transparent !important; }
  }
`;

const BoardDelBtn = styled.button`
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin-right: 2px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  display: grid;
  place-items: center;
  color: ${(p) => (p.$danger ? '#ef4444' : 'var(--mut)')};
  background: transparent;
  &:hover {
    background: ${(p) => (p.$danger
      ? 'color-mix(in srgb, #ef4444 18%, transparent)'
      : 'color-mix(in srgb, var(--blue) 16%, transparent)')};
    color: ${(p) => (p.$danger ? '#dc2626' : 'var(--blue)')};
  }
  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

const LogoBox = styled.div`
  width: 42px; height: 42px; border-radius: 14px;
  background: var(--grad);
  display: grid; place-items: center;
  position: relative; overflow: hidden;
  box-shadow: 0 8px 22px color-mix(in srgb, var(--blue) 45%, transparent);
  &::after {
    content: '';
    position: absolute; top: -30%; bottom: -30%; left: -40%; width: 40%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent);
    transform: skewX(-20deg);
    animation: ${shm} 3.4s ease-in-out infinite;
  }
`;

const TbBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  padding: 10px 15px; border-radius: 13px;
  font-weight: 800; font-size: 12.5px;
  border: 1px solid transparent; cursor: pointer; color: #fff;
  position: relative; overflow: hidden; transition: .22s;
  background: ${(p) =>
    p.$violet
      ? 'linear-gradient(135deg,#a78bfa,#7c3aed)'
      : 'linear-gradient(135deg,#38b6ff,#2488d8)'};
  box-shadow: ${(p) =>
    p.$violet
      ? '0 8px 22px color-mix(in srgb, var(--violet) 45%, transparent)'
      : '0 8px 22px color-mix(in srgb, var(--blue) 45%, transparent)'};
  &:hover { transform: translateY(-2px); }
  &::after {
    content: '';
    position: absolute; top: 0; bottom: 0; left: -50%; width: 40%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.35), transparent);
    transform: skewX(-20deg); transition: .4s;
  }
  &:hover::after { left: 120%; }
  .lbl { @media (max-width: 560px) { display: none; } }
  .spin { display: inline-block; transition: transform .55s; }
  &.spinning .spin { transform: rotate(360deg); }
`;

const ThemeBtn = styled.button`
  width: 42px; height: 42px; border-radius: 13px; border: none; cursor: pointer;
  display: grid; place-items: center; font-size: 18px; transition: .25s;
  background: ${(p) => (p.$light ? 'linear-gradient(135deg,#fbbf24,#f59e0b)' : 'linear-gradient(135deg,#33415e,#1c2740)')};
  box-shadow: ${(p) => (p.$light ? '0 8px 22px rgba(251,191,36,.4)' : '0 8px 22px rgba(0,0,0,.4)')};
  &:hover { transform: rotate(20deg) scale(1.1); }
`;

const Avatars = styled.div`
  display: flex;
  padding-left: 6px;
  @media (max-width: 560px) {
    display: none;
  }
`;

const Av = styled.span`
  width: ${(p) => p.$sm ? '22px' : '32px'};
  height: ${(p) => p.$sm ? '22px' : '32px'};
  border-radius: 50%;
  margin-left: ${(p) => (p.$first || p.$sm ? '0' : '-9px')};
  display: grid; place-items: center;
  font-size: ${(p) => (p.$sm ? '8.5px' : '10px')};
  font-weight: 800; color: #fff;
  background: ${(p) => p.$bg || AV_COLORS[0]};
  box-shadow: 0 4px 12px rgba(0,0,0,.3);
  border: 2px solid transparent;
  transition: .2s;
  flex: none;
  &:hover { transform: translateY(-4px) scale(1.08); z-index: 2; }
`;

const Wrap = styled.div`
  max-width: ${(p) => (p.$full ? 'none' : '1480px')};
  margin: 0 auto;
  padding: 18px 16px 46px;
  @media (max-width: 560px) { padding: 12px 10px 34px; }
`;

const Banner = styled.div`
  display: flex; align-items: center; gap: 12px; justify-content: center; flex-wrap: wrap;
  color: #fff; border-radius: 18px; padding: 14px 20px;
  font-weight: 800; font-size: 13.5px; margin-bottom: 16px;
  position: relative; overflow: hidden;
  background: linear-gradient(120deg,#2ea3f2,#7c5cf9,#2dd4a0,#2ea3f2);
  background-size: 260% 100%;
  animation: ${gradM} 9s linear infinite;
  box-shadow: 0 14px 40px color-mix(in srgb, var(--violet) 40%, transparent);
  &::after {
    content: '';
    position: absolute; top: 0; bottom: 0; width: 34%; left: -45%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.3), transparent);
    animation: ${shm} 4s ease-in-out infinite;
  }
  button {
    position: relative; z-index: 1;
    background: rgba(255,255,255,.2);
    border: 1px solid rgba(255,255,255,.45);
    border-radius: 11px; padding: 7px 14px;
    font-weight: 800; font-size: 12px; color: #fff; cursor: pointer;
    backdrop-filter: blur(6px); transition: .2s;
    &:hover { background: rgba(255,255,255,.35); transform: translateY(-1px); }
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
  @media (max-width: 1080px) { grid-template-columns: repeat(2, 1fr); }
`;

const HistoryBar = styled.div`
  ${glass};
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  padding: 12px 14px;
  margin-bottom: 16px;
  h4 {
    margin: 0;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: .6px;
    text-transform: uppercase;
    color: var(--head) !important;
    -webkit-text-fill-color: var(--head) !important;
    width: 100%;
  }
  select, input[type=date] {
    background: var(--panel2);
    border: 1.5px solid var(--stroke);
    border-radius: 12px;
    padding: 10px 12px;
    font-size: 13px;
    font-weight: 700;
    color: var(--txt);
    outline: none;
    min-width: 160px;
  }
  select { flex: 1; max-width: 280px; }
  input[type=date] { max-width: 170px; }
`;

const BoardFilterChip = styled.button`
  border: 1px solid var(--stroke);
  background: ${(p) => (p.$active ? 'color-mix(in srgb, var(--blue) 16%, transparent)' : 'var(--panel2)')};
  color: ${(p) => (p.$active ? 'var(--blue)' : 'var(--txt)')};
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 800;
  font-size: 12px;
  cursor: pointer;
`;

const StatChip = styled.button`
  ${glass};
  padding: 13px 16px;
  display: flex; flex-direction: column; gap: 2px;
  position: relative; overflow: hidden;
  animation: ${cardIn} .5s both;
  cursor: pointer;
  text-align: left;
  width: 100%;
  color: inherit;
  border: 1px solid var(--stroke);
  transition: transform .18s, box-shadow .2s, border-color .2s;
  outline: ${(p) => (p.$active ? '2px solid color-mix(in srgb, ' + (p.$sc || 'var(--blue)') + ' 70%, transparent)' : 'none')};
  outline-offset: 2px;
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 16px 36px rgba(0,0,0,.28);
    border-color: color-mix(in srgb, ${(p) => p.$sc || 'var(--blue)'} 45%, transparent);
  }
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    background: linear-gradient(90deg, ${(p) => p.$sc || 'var(--blue)'}, transparent);
  }
  b {
    font-family: 'JetBrains Mono', monospace;
    font-size: 22px; font-weight: 800;
    color: ${(p) => p.$sc || 'var(--blue)'};
    text-shadow: 0 0 18px color-mix(in srgb, ${(p) => p.$sc || 'var(--blue)'} 50%, transparent);
  }
  span {
    font-size: 10px; font-weight: 800; letter-spacing: 1.2px;
    color: var(--mut); text-transform: uppercase;
  }
`;

const FilterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  overflow-y: auto;
  padding: 4px 2px 12px;
  -webkit-overflow-scrolling: touch;
`;

const FilterCard = styled.div`
  background: var(--panel2);
  border: 1px solid var(--stroke);
  border-left: 4px solid ${(p) => p.$accent || 'var(--blue)'};
  border-radius: 16px;
  padding: 14px 15px;
  box-shadow: 0 6px 18px rgba(0,0,0,.18);
  .tt { font-weight: 700; font-size: 14px; margin-bottom: 8px; line-height: 1.4; color: var(--txt) !important; -webkit-text-fill-color: var(--txt) !important; }
  .meta {
    font-size: 12px; color: var(--mut); font-weight: 700;
    display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-bottom: 12px;
  }
  .badge {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    padding: 2px 8px;
    border-radius: 8px;
    border: 1px solid var(--stroke);
    background: var(--panel);
  }
  .acts { display: flex; gap: 8px; flex-wrap: wrap; }
`;

const FilterShell = styled.div`
  width: min(720px, 100%);
  height: min(92vh, 900px);
  max-height: 92vh;
  background: var(--solid);
  border: 1px solid var(--stroke);
  border-radius: 22px;
  box-shadow: 0 40px 90px rgba(0,0,0,.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--txt);
  animation: ${cardIn} .3s both;
  @media (max-width: 900px) {
    width: 100%;
    height: 100%;
    max-height: 100%;
    border-radius: 0;
  }
`;

const FilterHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--stroke);
  flex-shrink: 0;
  h3 {
    flex: 1;
    margin: 0;
    font-size: 15px;
    font-weight: 800;
    min-width: 0;
    color: var(--head) !important;
    -webkit-text-fill-color: var(--head) !important;
  }
`;

const FilterTabs = styled.div`
  display: flex;
  gap: 8px;
  padding: 10px 12px;
  overflow-x: auto;
  flex-shrink: 0;
  border-bottom: 1px solid var(--stroke);
  -webkit-overflow-scrolling: touch;
`;

const FilterTab = styled.button`
  flex: 0 0 auto;
  border: 1px solid ${(p) => (p.$active ? 'transparent' : 'var(--stroke)')};
  background: ${(p) => (p.$active ? p.$bg : 'var(--panel2)')};
  color: ${(p) => (p.$active ? '#fff' : 'var(--mut)')};
  border-radius: 12px;
  padding: 8px 12px;
  font-weight: 800;
  font-size: 11.5px;
  cursor: pointer;
  white-space: nowrap;
`;

const FilterNav = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 10px 12px 14px;
  border-top: 1px solid var(--stroke);
  flex-shrink: 0;
`;

const NavBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--stroke);
  background: var(--panel2);
  color: var(--txt);
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 800;
  font-size: 12.5px;
  cursor: pointer;
  &:disabled { opacity: .35; cursor: default; }
`;

const FilterBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 14px 0;
  touch-action: pan-y;
`;

const FILTER_ORDER = ['all', 'work', 'review', 'approved'];

const STATUS_LABEL = {
  new: 'Новое',
  in_progress: 'В работе',
  done: 'На проверке',
  approved: 'Утверждено',
};

const FILTER_META = {
  all: { title: 'Все активные задачи', short: 'Все', api: 'active', accent: 'var(--txt)' },
  work: { title: 'В работе', short: 'В работе', api: 'in_progress', accent: 'var(--amber)' },
  review: { title: 'На проверке', short: 'На проверке', api: 'done', accent: 'var(--violet)' },
  approved: { title: 'Утверждённые задачи', short: 'Утверждено', api: 'approved', accent: 'var(--green)' },
};

const Layout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 340px;
  gap: 18px;
  align-items: start;
  @media (max-width: 1080px) { grid-template-columns: 1fr; }
`;

const Composer = styled.div`
  ${glass};
  padding: ${(p) => (p.$collapsed ? '12px 16px' : '18px')};
  margin-bottom: ${(p) => (p.$collapsed ? '12px' : '18px')};
  animation: ${cardIn} .5s .1s both;
  transition: padding .2s, margin .2s;
`;

const ComposerHead = styled.button`
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  text-align: left;
  color: var(--head) !important;
  -webkit-text-fill-color: var(--head) !important;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: .6px;
  text-transform: uppercase;
  font-family: inherit;
  &::before {
    content: '';
    width: 9px;
    height: 9px;
    border-radius: 3px;
    background: var(--grad);
    box-shadow: 0 0 10px var(--blue);
    flex-shrink: 0;
  }
  span { flex: 1; }
  .chev {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 9px;
    background: var(--panel2);
    border: 1px solid var(--stroke);
    color: var(--head);
    flex-shrink: 0;
  }
`;

const ComposerBody = styled.div`
  margin-top: 13px;
`;

const CRow = styled.div`
  display: flex; gap: 10px; flex-wrap: wrap;
  input[type=text], select {
    background: var(--panel2);
    border: 1.5px solid var(--stroke);
    border-radius: 13px;
    padding: 12px 15px;
    font-size: 13.5px;
    color: var(--txt);
    outline: none;
  }
  input[type=text] {
    flex: 1; min-width: 200px;
    &:focus {
      border-color: var(--blue);
      box-shadow: 0 0 0 4px color-mix(in srgb, var(--blue) 18%, transparent), 0 0 20px color-mix(in srgb, var(--blue) 25%, transparent);
    }
  }
  select {
    font-weight: 700; max-width: 200px;
    @media (max-width: 560px) { max-width: 100%; flex: 1; }
    option { background: var(--solid); color: var(--txt); }
  }
`;

const ComposerDesc = styled.textarea`
  width: 100%;
  margin-top: 10px;
  min-height: 72px;
  resize: vertical;
  background: var(--panel2);
  border: 1.5px solid var(--stroke);
  border-radius: 13px;
  padding: 12px 15px;
  font-size: 13px;
  font-weight: 600;
  color: var(--txt);
  font-family: inherit;
  outline: none;
  line-height: 1.45;
  &:focus {
    border-color: var(--blue);
    box-shadow: 0 0 0 4px color-mix(in srgb, var(--blue) 18%, transparent);
  }
`;

const CreateBtn = styled.button`
  display: flex; align-items: center; gap: 8px;
  background: linear-gradient(135deg,#34e3b0,#14a87c);
  color: #04150c; font-weight: 800; font-size: 13px;
  border: none; border-radius: 13px; padding: 11px 20px; cursor: pointer;
  box-shadow: 0 10px 26px color-mix(in srgb, var(--green) 45%, transparent);
  transition: .22s; position: relative; overflow: hidden;
  &:hover { transform: translateY(-2px); filter: brightness(1.08); }
  &::after {
    content: '';
    position: absolute; top: 0; bottom: 0; left: -50%; width: 40%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.5), transparent);
    transform: skewX(-20deg); transition: .4s;
  }
  &:hover::after { left: 120%; }
  @media (max-width: 560px) { width: 100%; justify-content: center; }
`;

const Board = styled.div`
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding: 4px 2px 16px;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
  @media (max-width: 900px) {
    flex-direction: column;
    overflow-x: visible;
    scroll-snap-type: none;
    gap: 16px;
  }
`;

const Col = styled.div`
  --cc: ${(p) => (p.$kind === 'new' ? 'var(--blue)' : p.$kind === 'work' ? 'var(--amber)' : 'var(--green)')};
  flex: 1 0 280px;
  min-width: 260px;
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 300px);
  min-height: 280px;
  scroll-snap-align: start;
  background: var(--panel);
  backdrop-filter: blur(18px);
  border: 1px solid var(--stroke);
  box-shadow: ${(p) => (p.$over ? '0 0 0 3px color-mix(in srgb, var(--cc) 55%, transparent), 0 0 40px color-mix(in srgb, var(--cc) 30%, transparent)' : 'var(--shadow)')};
  position: relative;
  overflow: visible;
  transition: box-shadow .25s;
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    border-radius: 20px 20px 0 0;
    background: linear-gradient(90deg, var(--cc), transparent 80%);
    box-shadow: 0 0 18px var(--cc);
    z-index: 2;
    pointer-events: none;
  }
  &::after {
    content: '';
    position: absolute; inset: 0;
    border-radius: 20px;
    background: radial-gradient(340px 140px at 20% 0%, color-mix(in srgb, var(--cc) 12%, transparent), transparent 70%);
    pointer-events: none;
  }
  @media (max-width: 900px) {
    flex: none;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    max-height: none;
    min-height: 0;
  }
`;

const ColHead = styled.div`
  display: flex; align-items: center; gap: 10px;
  padding: 15px 16px 11px; font-weight: 800; font-size: 13px;
  letter-spacing: .3px; position: relative; z-index: 1;
  color: var(--head) !important;
  -webkit-text-fill-color: var(--head) !important;
  .dot {
    width: 10px; height: 10px; border-radius: 50%;
    background: var(--cc); box-shadow: 0 0 12px var(--cc);
    animation: ${pul} 2.2s infinite;
  }
  .cnt {
    margin-left: auto;
    background: color-mix(in srgb, var(--cc) 16%, transparent);
    border: 1px solid color-mix(in srgb, var(--cc) 40%, transparent);
    border-radius: 9px; padding: 2px 10px;
    font-size: 11px; font-weight: 800; color: var(--cc);
    font-family: 'JetBrains Mono', monospace;
  }
`;

const ColBody = styled.div`
  flex: 1; overflow-y: auto; overflow-x: hidden;
  padding: 4px 12px 28px;
  display: flex; flex-direction: column; gap: 12px;
  position: relative; z-index: 1;
`;

const Empty = styled.div`
  border: 2px dashed color-mix(in srgb, var(--cc) 45%, transparent);
  border-radius: 15px; padding: 22px; text-align: center;
  color: var(--mut); font-size: 12px; font-weight: 700; opacity: .8;
`;

const Card = styled.div`
  background: linear-gradient(165deg, color-mix(in srgb, var(--cc) 7%, var(--panel2)), var(--panel2));
  border: 1px solid var(--stroke);
  border-radius: 16px; padding: 14px 15px 18px;
  box-shadow: 0 6px 18px rgba(0,0,0,.25);
  cursor: pointer; position: relative; overflow: visible;
  transition: transform .18s, box-shadow .25s, border-color .2s;
  animation: ${(p) => (p.$justMoved ? jump : cardIn)} ${(p) => (p.$justMoved ? '.5s' : '.4s')} cubic-bezier(.2,.9,.3,1.2) both;
  opacity: ${(p) => (p.$dragging ? .4 : 1)};
  &::before {
    content: '';
    position: absolute; left: 0; top: 12px; bottom: 12px; width: 3px;
    border-radius: 3px; background: var(--cc); box-shadow: 0 0 10px var(--cc);
    pointer-events: none;
  }
  &:hover {
    border-color: color-mix(in srgb, var(--cc) 45%, transparent);
    box-shadow: 0 16px 36px rgba(0,0,0,.35), 0 0 24px color-mix(in srgb, var(--cc) 18%, transparent);
  }
  .tt { font-weight: 700; font-size: 13.5px; line-height: 1.45; margin-bottom: 9px; padding-left: 6px; color: var(--txt) !important; -webkit-text-fill-color: var(--txt) !important; }
  .meta { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-bottom: 10px; padding-left: 6px; }
  .who { display: flex; align-items: center; gap: 7px; font-size: 11.5px; color: var(--mut); font-weight: 700; }
  .time {
    display: flex; align-items: center; gap: 5px;
    font-family: 'JetBrains Mono', monospace; font-size: 10.5px; font-weight: 700;
    background: var(--panel2); border: 1px solid var(--stroke);
    border-radius: 8px; padding: 3px 8px; color: var(--mut);
    &.late {
      color: var(--red);
      border-color: color-mix(in srgb, var(--red) 50%, transparent);
      background: color-mix(in srgb, var(--red) 12%, transparent);
      animation: ${pul} 1.3s infinite;
    }
  }
  .timer {
    display: inline-flex; align-items: center; gap: 5px;
    font-family: 'JetBrains Mono', monospace; font-size: 11px; font-weight: 800;
    border-radius: 8px; padding: 3px 8px;
    border: 1px solid color-mix(in srgb, var(--amber) 45%, transparent);
    background: color-mix(in srgb, var(--amber) 14%, transparent);
    color: var(--amber);
    &.over {
      color: var(--red);
      border-color: color-mix(in srgb, var(--red) 50%, transparent);
      background: color-mix(in srgb, var(--red) 14%, transparent);
      animation: ${pul} 1.1s infinite;
    }
  }
  .dur {
    display: inline-flex; align-items: center; gap: 4px;
    font-size: 10.5px; font-weight: 700; color: var(--mut);
    background: var(--panel2); border: 1px solid var(--stroke);
    border-radius: 8px; padding: 3px 8px;
  }
  .acts {
    display: flex; gap: 8px; flex-wrap: wrap; align-items: center;
    padding: 2px 6px 2px 6px;
    position: relative;
    z-index: 2;
  }
  .from {
    width: 100%; padding-left: 6px; margin: -4px 0 8px;
    font-size: 11px; color: var(--mut); font-weight: 700;
  }
  .files {
    display: flex; flex-wrap: wrap; gap: 6px; padding-left: 6px; margin-bottom: 10px;
  }
`;

const FileChip = styled.a`
  display: inline-flex; align-items: center; gap: 5px;
  max-width: 100%;
  font-size: 10.5px; font-weight: 700;
  color: var(--blue);
  background: color-mix(in srgb, var(--blue) 10%, transparent);
  border: 1px solid color-mix(in srgb, var(--blue) 28%, transparent);
  border-radius: 8px; padding: 4px 8px; text-decoration: none;
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 140px; }
`;

const FilePick = styled.label`
  display: inline-flex; align-items: center; gap: 6px;
  border: 1px dashed var(--stroke);
  background: var(--panel2);
  color: var(--txt) !important;
  -webkit-text-fill-color: var(--txt) !important;
  border-radius: 12px;
  padding: 10px 12px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  svg {
    color: var(--txt) !important;
    stroke: var(--txt) !important;
    flex-shrink: 0;
  }
  input { display: none; }
`;

const FileListMini = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px;
  button {
    border: none; background: color-mix(in srgb, var(--red) 14%, transparent);
    color: var(--red); border-radius: 8px; padding: 4px 8px;
    font-size: 11px; font-weight: 800; cursor: pointer;
  }
  span {
    font-size: 11px; font-weight: 700; color: var(--mut);
    background: var(--panel2); border: 1px solid var(--stroke);
    border-radius: 8px; padding: 4px 8px;
  }
`;

const ActBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  border-radius: 10px; padding: 8px 12px;
  font-size: 11.5px; font-weight: 800; border: none; cursor: pointer;
  transition: .18s; position: relative; z-index: 2; flex-shrink: 0;
  &:hover { transform: translateY(-1px); filter: brightness(1.08); }
  ${(p) =>
    p.$kind === 'toWork' &&
    css`
      background: linear-gradient(135deg,#fcd34d,#f59e0b);
      color: #241a03;
      box-shadow: 0 6px 16px color-mix(in srgb, var(--amber) 40%, transparent);
    `}
  ${(p) =>
    p.$kind === 'toDone' &&
    css`
      background: linear-gradient(135deg,#34e3b0,#14a87c);
      color: #04150c;
      box-shadow: 0 6px 16px color-mix(in srgb, var(--green) 40%, transparent);
    `}
  ${(p) =>
    p.$kind === 'del' &&
    css`
      background: color-mix(in srgb, var(--red) 14%, transparent);
      color: var(--red);
      border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
      &:hover { background: var(--red); color: #fff; }
    `}
  ${(p) =>
    p.$kind === 'ret' &&
    css`
      background: color-mix(in srgb, var(--red) 14%, transparent);
      color: var(--red);
      border: 1px solid color-mix(in srgb, var(--red) 30%, transparent);
      &:hover { background: var(--red); color: #fff; }
    `}
  ${(p) =>
    p.$kind === 'ok' &&
    css`
      background: linear-gradient(135deg,#34e3b0,#14a87c);
      color: #04150c;
      box-shadow: 0 6px 16px color-mix(in srgb, var(--green) 40%, transparent);
    `}
`;

const Review = styled.aside`
  ${glass};
  padding: 18px 18px 22px;
  position: sticky; top: 86px;
  animation: ${cardIn} .5s .15s both;
  overflow: visible;
  &::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 3px;
    border-radius: 20px 20px 0 0;
    background: linear-gradient(90deg, var(--violet), transparent 80%);
    box-shadow: 0 0 18px var(--violet);
    pointer-events: none;
  }
  h3 {
    font-size: 13px; font-weight: 800; letter-spacing: .5px;
    display: flex; align-items: center; gap: 9px;
    margin-bottom: 5px; text-transform: uppercase;
    color: var(--head) !important;
    -webkit-text-fill-color: var(--head) !important;
    &::before {
      content: ''; width: 9px; height: 9px; border-radius: 3px;
      background: var(--violet); box-shadow: 0 0 10px var(--violet);
      flex-shrink: 0;
    }
  }
  .sub {
    font-size: 11px; color: var(--mut) !important; margin-bottom: 13px;
    display: flex; gap: 9px; align-items: center; font-weight: 600;
    -webkit-text-fill-color: var(--mut) !important;
  }
  @media (max-width: 1080px) { position: static; }
`;

const DoneChip = styled.span`
  background: color-mix(in srgb, var(--green) 14%, transparent);
  color: var(--green);
  border: 1px solid color-mix(in srgb, var(--green) 40%, transparent);
  border-radius: 9px; padding: 3px 10px;
  font-weight: 800; font-size: 10.5px;
  font-family: 'JetBrains Mono', monospace;
  animation: ${(p) => (p.$bump ? jump : 'none')} .5s;
`;

const RCard = styled.div`
  background: var(--panel2);
  border: 1px solid var(--stroke);
  border-left: 4px solid var(--violet);
  border-radius: 15px; padding: 13px 14px 16px; margin-bottom: 11px;
  animation: ${cardIn} .4s both; transition: .2s; position: relative; overflow: visible;
  &:hover {
    transform: translateX(2px);
    border-color: color-mix(in srgb, var(--violet) 45%, transparent);
    box-shadow: 0 10px 26px rgba(0,0,0,.3);
  }
  .tt { font-weight: 700; font-size: 12.5px; line-height: 1.5; margin-bottom: 6px; color: var(--txt) !important; -webkit-text-fill-color: var(--txt) !important; }
  .who { font-size: 11px; color: var(--mut); font-weight: 700; margin-bottom: 6px; }
  .from { font-size: 11px; color: var(--mut); font-weight: 700; margin-bottom: 10px; }
  .okmark {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 11px; font-weight: 800; color: var(--green); margin-bottom: 8px;
  }
  .acts { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; position: relative; z-index: 2; padding-bottom: 2px; }
`;

const TaskDetailShell = styled.div`
  width: min(560px, 100%);
  max-height: min(90vh, 820px);
  background: var(--solid);
  border: 1px solid var(--stroke);
  border-radius: 22px;
  box-shadow: 0 40px 90px rgba(0,0,0,.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: var(--txt);
  animation: ${cardIn} .3s both;
  @media (max-width: 900px) {
    width: 100%;
    max-height: 100%;
    height: 100%;
    border-radius: 0;
  }
`;

const TaskDetailBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DescBox = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 13.5px;
  line-height: 1.55;
  font-weight: 600;
  color: var(--txt);
  background: var(--panel2);
  border: 1px solid var(--stroke);
  border-radius: 14px;
  padding: 14px;
  min-height: 120px;
`;

const DescArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  resize: vertical;
  border-radius: 14px;
  border: 1px solid var(--stroke);
  background: var(--panel2);
  color: var(--txt);
  padding: 12px 14px;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.5;
  font-family: inherit;
  outline: none;
  &:focus { border-color: color-mix(in srgb, var(--blue) 55%, transparent); }
`;

const TitleInput = styled.input`
  width: 100%;
  border-radius: 12px;
  border: 1px solid var(--stroke);
  background: var(--panel2);
  color: var(--txt);
  padding: 11px 13px;
  font-size: 15px;
  font-weight: 800;
  outline: none;
  &:focus { border-color: color-mix(in srgb, var(--blue) 55%, transparent); }
`;

const MetaLine = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: var(--mut);
  display: flex;
  flex-wrap: wrap;
  gap: 8px 14px;
`;

const OpenHint = styled.div`
  font-size: 10.5px;
  font-weight: 800;
  color: var(--blue);
  padding-left: 6px;
  margin-bottom: 8px;
  opacity: .85;
`;

const ModalBack = styled.div`
  position: fixed; inset: 0;
  background: rgba(5,8,16,.6);
  backdrop-filter: blur(8px);
  display: grid; place-items: center;
  z-index: 2147483000; padding: 16px;
`;

const MBox = styled.div`
  width: 430px; max-width: 100%;
  background: var(--solid);
  border: 1px solid var(--stroke);
  border-radius: 22px;
  box-shadow: 0 40px 90px rgba(0,0,0,.5);
  padding: 22px; animation: ${cardIn} .3s both;
  max-height: 84vh; overflow-y: auto;
  color: var(--txt);
  h3 {
    display: flex; justify-content: space-between; align-items: center;
    font-size: 15px; margin-bottom: 15px;
    color: var(--head) !important;
    -webkit-text-fill-color: var(--head) !important;
  }
`;

const PRow = styled.div`
  display: flex; align-items: center; gap: 11px;
  padding: 10px; border-radius: 13px; transition: .15s;
  &:hover { background: var(--panel2); }
  b { font-size: 13px; flex: 1; }
`;

const PickBtn = styled.button`
  background: color-mix(in srgb, var(--blue) 15%, transparent);
  color: var(--blue);
  border: 1px solid color-mix(in srgb, var(--blue) 40%, transparent);
  border-radius: 10px; padding: 7px 13px;
  font-weight: 800; font-size: 11px; cursor: pointer;
`;

const RmBtn = styled.button`
  color: var(--mut); width: 28px; height: 28px; border-radius: 9px;
  border: none; background: none; cursor: pointer;
  &:hover { background: color-mix(in srgb, var(--red) 15%, transparent); color: var(--red); }
`;

const MAdd = styled.div`
  display: flex; gap: 8px; margin-top: 13px; flex-wrap: wrap;
  select, input {
    flex: 1; min-width: 140px;
    background: var(--panel2); border: 1.5px solid var(--stroke);
    border-radius: 12px; padding: 11px 13px; color: var(--txt); outline: none;
  }
  button {
    background: var(--grad); color: #fff; border: none;
    border-radius: 12px; padding: 11px 17px; font-weight: 800; font-size: 12.5px;
    cursor: pointer; box-shadow: 0 8px 20px color-mix(in srgb, var(--blue) 40%, transparent);
  }
`;

const ToastHost = styled.div`
  position: fixed; bottom: 20px; left: 50%; transform: translateX(-50%);
  display: flex; flex-direction: column; gap: 8px; z-index: 2147483100; align-items: center;
`;

const Toast = styled.div`
  background: var(--solid); border: 1px solid var(--stroke);
  border-left: 4px solid ${(p) => (p.$cls === 'red' ? 'var(--red)' : p.$cls === 'blue' ? 'var(--blue)' : 'var(--green)')};
  border-radius: 14px; padding: 11px 20px;
  font-size: 12.5px; font-weight: 800;
  box-shadow: 0 18px 44px rgba(0,0,0,.4);
  animation: ${tIn} .35s cubic-bezier(.2,.9,.3,1.3) both;
  max-width: 92vw; color: var(--txt);
`;

const Err = styled.div`
  margin-bottom: 12px; color: #b91c1c; background: #fef2f2;
  padding: 10px 12px; border-radius: 10px; font-weight: 700;
`;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' };
}

function authHeadersMultipart() {
  return { Authorization: `Bearer ${localStorage.getItem('token')}` };
}

/** Как в Tasks: показать настоящее имя файла (кириллица/латиница), починить «кракозябры» multer. */
function displayFileName(name) {
  if (!name) return 'file';
  let s = String(name);
  try {
    if (/%[0-9A-Fa-f]{2}/.test(s)) s = decodeURIComponent(s);
  } catch (_) {}
  if (/[ÐÑ]/.test(s)) {
    try {
      const bytes = Uint8Array.from(s, (c) => c.charCodeAt(0) & 0xff);
      const fixed = new TextDecoder('utf-8').decode(bytes);
      if (fixed && !fixed.includes('\uFFFD')) s = fixed;
    } catch (_) {}
  }
  return s;
}

function displayName(m) {
  return (m?.fio || m?.username || `ID ${m?.user_id}` || '—').trim();
}

function initials(name) {
  return String(name || '?')
    .split(/\s+/)
    .map((x) => x[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function avColor(i) {
  return AV_COLORS[i % AV_COLORS.length];
}

function isLate(tk) {
  if (!tk.time_hint || tk.status === 'done' || tk.status === 'approved') return false;
  const n = new Date();
  const [h, m] = tk.time_hint.split(':').map(Number);
  return n.getHours() > h || (n.getHours() === h && n.getMinutes() >= m);
}

function formatCountdown(remainingMs) {
  const neg = remainingMs < 0;
  const totalSec = Math.floor(Math.abs(remainingMs) / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const body = h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return neg ? `−${body}` : body;
}

function getTaskCountdown(tk, nowMs = Date.now()) {
  const dur = Number(tk?.duration_minutes) || 0;
  if (!dur || !tk?.started_at) return null;
  if (tk.status !== 'in_progress') return null;
  const start = new Date(tk.started_at).getTime();
  if (!Number.isFinite(start)) return null;
  const end = start + dur * 60 * 1000;
  const remainingMs = end - nowMs;
  return { remainingMs, overdue: remainingMs < 0, durationMin: dur };
}

/** План/факт для сдачи: late если факт > план. */
function getDoneTimingPreview(tk, nowMs = Date.now()) {
  const plan = Number(tk?.duration_minutes) || 0;
  if (!plan || !tk?.started_at) {
    return { needsReason: false, plan: plan || null, actual: null };
  }
  const start = new Date(tk.started_at).getTime();
  if (!Number.isFinite(start)) return { needsReason: false, plan, actual: null };
  const actual = Math.max(0, Math.round((nowMs - start) / 60000));
  return { needsReason: actual > plan, plan, actual };
}

function formatTimingDelta(plan, actual) {
  if (!Number.isFinite(plan) || !Number.isFinite(actual)) return '';
  const d = actual - plan;
  if (d === 0) return '±0';
  return d > 0 ? `+${d}` : `${d}`;
}

function TaskTimerBadge({ tk, nowMs }) {
  const cd = getTaskCountdown(tk, nowMs);
  if (cd) {
    return (
      <span className={`timer${cd.overdue ? ' over' : ''}`} title={cd.overdue ? 'Время вышло' : 'Осталось времени'}>
        ⏱ {formatCountdown(cd.remainingMs)}
      </span>
    );
  }
  const dur = Number(tk?.duration_minutes) || 0;
  if (dur > 0 && (tk.status === 'new' || !tk.started_at)) {
    return <span className="dur" title="Лимит на выполнение">⏱ {dur} мин</span>;
  }
  return null;
}

function TaskTimingResultBadge({ tk }) {
  const plan = Number(tk?.duration_minutes) || 0;
  const actual = tk?.actual_minutes != null ? Number(tk.actual_minutes) : null;
  const result = tk?.timing_result;
  if (!result && actual == null) return null;
  if (tk.status !== 'done' && tk.status !== 'approved') return null;

  const reasonLabel = DELAY_REASONS.find((r) => r.id === tk.delay_reason)?.label;
  const titleParts = [
    plan ? `План: ${plan} мин` : null,
    actual != null ? `Факт: ${actual} мин` : null,
    result ? TIMING_LABEL[result] || result : null,
    reasonLabel ? `Причина: ${reasonLabel}` : null,
    tk.delay_note ? tk.delay_note : null,
  ].filter(Boolean);

  if (plan && actual != null) {
    const cls = result === 'late' ? 'timer over' : result === 'early' ? 'dur' : 'dur';
    return (
      <span className={cls} title={titleParts.join(' · ')}>
        ⏱ {plan}→{actual} ({formatTimingDelta(plan, actual)})
      </span>
    );
  }
  if (actual != null) {
    return <span className="dur" title={titleParts.join(' · ')}>⏱ факт {actual} мин</span>;
  }
  return null;
}

function useNowTick(enabled, ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [enabled, ms]);
  return now;
}

function confetti(x, y) {
  for (let i = 0; i < 28; i++) {
    const p = document.createElement('i');
    p.style.cssText = `position:fixed;width:8px;height:12px;border-radius:2px;z-index:2147483300;pointer-events:none;left:${x}px;top:${y}px;background:${['#2dd4a0','#38b6ff','#8b5cf6','#fbbf24','#fb7185'][i % 5]}`;
    document.body.appendChild(p);
    const a = Math.random() * Math.PI * 2;
    const v = 50 + Math.random() * 110;
    p.animate(
      [
        { transform: 'translate(0,0) rotate(0)', opacity: 1 },
        { transform: `translate(${Math.cos(a) * v}px,${Math.sin(a) * v + 90}px) rotate(${Math.random() * 720 - 360}deg)`, opacity: 0 },
      ],
      { duration: 850 + Math.random() * 550, easing: 'cubic-bezier(.2,.8,.3,1)' }
    ).onfinish = () => p.remove();
  }
}

function Particles({ light }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d');
    let pts = [];
    let raf = 0;
    const resize = () => {
      const parent = c.parentElement;
      c.width = parent?.clientWidth || window.innerWidth;
      c.height = parent?.clientHeight || window.innerHeight;
      pts = Array.from({ length: 44 }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        r: Math.random() * 1.5 + 0.4,
      }));
    };
    resize();
    const onResize = () => resize();
    window.addEventListener('resize', onResize);
    const loop = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      const fill = light ? 'rgba(60,100,180,.28)' : 'rgba(120,180,255,.35)';
      pts.forEach((p) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > c.width) p.vx *= -1;
        if (p.y < 0 || p.y > c.height) p.vy *= -1;
        ctx.fillStyle = fill;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 7); ctx.fill();
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const a = pts[i], b = pts[j];
          const dx = a.x - b.x, dy = a.y - b.y, d = dx * dx + dy * dy;
          if (d < 11000) {
            ctx.strokeStyle = light ? `rgba(60,100,180,${(1 - d / 11000) * 0.08})` : `rgba(120,180,255,${(1 - d / 11000) * 0.1})`;
            ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', onResize); };
  }, [light]);
  return <BgCanvas ref={ref} />;
}

export default function KanbanModal({ open, onClose, isMobile = false }) {
  const { state: appState } = useApp();
  const godMode = !!appState?.user?.godMode;
  const [light, setLight] = useState(() => localStorage.getItem('kanban-theme') === 'light');
  const [fullscreen, setFullscreen] = useState(() => localStorage.getItem('kanban-fullscreen') === '1');
  const [loading, setLoading] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [error, setError] = useState('');
  const [board, setBoard] = useState(null);
  const [users, setUsers] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newDuration, setNewDuration] = useState('');
  const [newAssignee, setNewAssignee] = useState('');
  const [modal, setModal] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [detailSaving, setDetailSaving] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [doneToday, setDoneToday] = useState(0);
  const [filterKey, setFilterKey] = useState(null); // 'all' | 'work' | 'review' | 'approved'
  const [filterTasks, setFilterTasks] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [lastMoved, setLastMoved] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [bump, setBump] = useState(false);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState('member');
  const [newFiles, setNewFiles] = useState([]);
  const [boardUserId, setBoardUserId] = useState('');
  const [histUserId, setHistUserId] = useState('');
  const [histDate, setHistDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [histOpen, setHistOpen] = useState(false);
  const [histLoading, setHistLoading] = useState(false);
  const [histTasks, setHistTasks] = useState([]);
  const [composerOpen, setComposerOpen] = useState(() => localStorage.getItem('kanban-composer-open') !== '0');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [donePrompt, setDonePrompt] = useState(null); // { task, toastMsg, toastCls }
  const [delayReason, setDelayReason] = useState('');
  const [delayNote, setDelayNote] = useState('');
  const [doneSubmitting, setDoneSubmitting] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [analyticsRange, setAnalyticsRange] = useState('week'); // week | month
  const [analyticsAssignee, setAnalyticsAssignee] = useState('');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsError, setAnalyticsError] = useState('');
  const [boardId, setBoardId] = useState(() => Number(localStorage.getItem('kanban-board-id')) || 0);
  const [boardsMenuOpen, setBoardsMenuOpen] = useState(false);
  const [boardForm, setBoardForm] = useState(null); // { mode: 'create' | 'rename', name }
  const [boardSaving, setBoardSaving] = useState(false);
  const finePointer = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(pointer:fine)').matches,
    []
  );

  const toast = useCallback((m, cls) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, m, cls }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2600);
  }, []);

  const boardQs = (extra = {}) => {
    const q = new URLSearchParams(extra);
    if (boardId) q.set('boardId', String(boardId));
    return q.toString();
  };

  const openFilter = async (key) => {
    const meta = FILTER_META[key];
    if (!meta) return;
    setFilterKey(key);
    setFilterLoading(true);
    setFilterTasks([]);
    try {
      const r = await fetch(`/api/kanban/tasks?${boardQs({ status: meta.api })}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить список');
      setFilterTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (e) {
      toast(e.message, 'red');
      setFilterKey(null);
    } finally {
      setFilterLoading(false);
    }
  };

  const reloadFilter = async (key = filterKey) => {
    if (!key) return;
    const meta = FILTER_META[key];
    if (!meta) return;
    try {
      const r = await fetch(`/api/kanban/tasks?${boardQs({ status: meta.api })}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить список');
      setFilterTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (e) {
      toast(e.message, 'red');
    }
  };

  const runFilterAction = async (fn) => {
    try {
      await fn();
      await reloadFilter();
    } catch (e) {
      if (e) toast(e.message || 'Ошибка', 'red');
    }
  };

  const filterIdx = FILTER_ORDER.indexOf(filterKey);
  const goFilter = (dir) => {
    const next = FILTER_ORDER[filterIdx + dir];
    if (next) openFilter(next);
  };

  const swipeRef = useRef({ x: 0, y: 0, active: false });
  const onFilterTouchStart = (e) => {
    const t = e.changedTouches?.[0];
    if (!t) return;
    swipeRef.current = { x: t.clientX, y: t.clientY, active: true };
  };
  const onFilterTouchEnd = (e) => {
    if (!swipeRef.current.active) return;
    const t = e.changedTouches?.[0];
    swipeRef.current.active = false;
    if (!t) return;
    const dx = t.clientX - swipeRef.current.x;
    const dy = t.clientY - swipeRef.current.y;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy)) return;
    if (dx < 0) goFilter(1);
    else goFilter(-1);
  };

  const loadBoard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const qs = boardId ? `?boardId=${boardId}` : '';
      const r = await fetch(`/api/kanban/board${qs}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить канбан');
      setBoard(data);
      // Сервер сам решает, какая доска доступна, если сохранённая исчезла
      if (data.board?.id && Number(data.board.id) !== boardId) setBoardId(Number(data.board.id));
      if (typeof data.approvedCount === 'number') setDoneToday(data.approvedCount);
      setNewAssignee((prev) => {
        if (!data.me?.isSupervisor && data.me?.userId) return String(data.me.userId);
        return prev || (data.workers?.[0] ? String(data.workers[0].user_id) : data.supervisors?.[0] ? String(data.supervisors[0].user_id) : '');
      });
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [boardId]);

  useEffect(() => { if (open) loadBoard(); }, [open, loadBoard]);

  useEffect(() => {
    if (!open) return undefined;
    const onBoard = (payload) => {
      const bid = Number(payload?.boardId) || 0;
      if (bid && boardId && bid !== boardId) return; // событие с другой доски
      loadBoard();
    };
    const attach = (s) => {
      if (!s) return () => {};
      s.on('kanban_board_updated', onBoard);
      s.on('kanban_task_status', onBoard);
      return () => {
        s.off('kanban_board_updated', onBoard);
        s.off('kanban_task_status', onBoard);
      };
    };
    let detach = attach(typeof window !== 'undefined' ? window.socket : null);
    const timer = setInterval(() => {
      if (!detach && window.socket) detach = attach(window.socket);
    }, 800);
    return () => {
      clearInterval(timer);
      if (typeof detach === 'function') detach();
    };
  }, [open, loadBoard, boardId]);

  useEffect(() => {
    localStorage.setItem('kanban-composer-open', composerOpen ? '1' : '0');
  }, [composerOpen]);

  useEffect(() => {
    if (boardId) localStorage.setItem('kanban-board-id', String(boardId));
    else localStorage.removeItem('kanban-board-id');
  }, [boardId]);

  useEffect(() => {
    localStorage.setItem('kanban-theme', light ? 'light' : 'dark');
  }, [light]);

  useEffect(() => {
    localStorage.setItem('kanban-fullscreen', fullscreen ? '1' : '0');
  }, [fullscreen]);

  // Пока канбан открыт — прячем верхние часы мессенджера (они перекрывают доску)
  useEffect(() => {
    if (!open) return undefined;
    document.body.classList.add('kanban-open');
    return () => document.body.classList.remove('kanban-open');
  }, [open]);

  // Полный экран: #app-modal-root изолирован и лежит ниже сайдбара (z-index 1000000),
  // поэтому поднимаем его целиком, иначе доска уйдёт под левую панель.
  useEffect(() => {
    const on = open && fullscreen && !isMobile;
    if (!on) return undefined;
    document.body.classList.add('kanban-fullscreen');
    return () => document.body.classList.remove('kanban-fullscreen');
  }, [open, fullscreen, isMobile]);

  useEffect(() => {
    if (!open || !modal) return;
    fetch('/api/users', { headers: authHeaders() })
      .then((r) => r.json())
      .then((list) => setUsers(Array.isArray(list) ? list : []))
      .catch(() => setUsers([]));
  }, [open, modal]);

  const members = useMemo(
    () => [...(board?.supervisors || []), ...(board?.workers || [])],
    [board]
  );
  const memberIndex = useMemo(() => {
    const map = {};
    members.forEach((m, i) => { map[Number(m.user_id)] = i; });
    return map;
  }, [members]);

  const tasks = board?.tasks || [];
  const approvedRecent = board?.approvedRecent || [];
  const me = board?.me;
  const visibleTasks = useMemo(() => {
    const selected = Number(boardUserId);
    const selfOnly = !(me?.isSupervisor || me?.canManage);
    const uid = selected || (selfOnly ? Number(me?.userId) : 0);
    if (!uid) return tasks;
    return tasks.filter((t) => Number(t.assignee_id) === uid);
  }, [tasks, boardUserId, me]);
  const byCol = useMemo(() => ({
    new: visibleTasks.filter((t) => t.status === 'new'),
    work: visibleTasks.filter((t) => t.status === 'in_progress'),
    review: visibleTasks.filter((t) => t.status === 'done'),
  }), [visibleTasks]);

  const needTimerTick = useMemo(
    () => (
      visibleTasks.some((t) => t.status === 'in_progress' && Number(t.duration_minutes) > 0 && t.started_at)
      || filterTasks.some((t) => t.status === 'in_progress' && Number(t.duration_minutes) > 0 && t.started_at)
    ),
    [visibleTasks, filterTasks]
  );
  const nowTick = useNowTick(open && needTimerTick, 1000);

  const leader = (board?.supervisors || [])[0];
  const memberIds = new Set(members.map((m) => Number(m.user_id)));
  const canSeeHistory = !!(me?.isSupervisor || me?.canManage || me?.canApprove);
  const canSeeAnalytics = !!(me?.isSupervisor || me?.canManage);
  const boardName = board?.board?.name || 'Загрузка…';
  const boardsList = Array.isArray(board?.boards) ? board.boards : [];

  const loadAnalytics = useCallback(async () => {
    if (!canSeeAnalytics) return;
    setAnalyticsLoading(true);
    setAnalyticsError('');
    try {
      const qs = new URLSearchParams({ range: analyticsRange });
      if (analyticsAssignee) qs.set('assigneeId', String(analyticsAssignee));
      if (boardId) qs.set('boardId', String(boardId));
      const r = await fetch(`/api/kanban/analytics?${qs}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка сводки');
      setAnalyticsData(data);
    } catch (e) {
      setAnalyticsError(e.message || 'Ошибка сводки');
      setAnalyticsData(null);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [canSeeAnalytics, analyticsRange, analyticsAssignee, boardId]);

  useEffect(() => {
    if (open && analyticsOpen && canSeeAnalytics) loadAnalytics();
  }, [open, analyticsOpen, canSeeAnalytics, loadAnalytics]);


  const openHistory = async () => {
    const uid = histUserId || boardUserId;
    if (!uid || !histDate) {
      toast('Выберите сотрудника и дату', 'red');
      return;
    }
    if (!histUserId && boardUserId) setHistUserId(boardUserId);
    setHistOpen(true);
    setHistLoading(true);
    setHistTasks([]);
    try {
      const q = new URLSearchParams({ assigneeId: String(uid), date: histDate });
      if (boardId) q.set('boardId', String(boardId));
      const r = await fetch(`/api/kanban/history?${q}`, { headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось загрузить историю');
      setHistTasks(Array.isArray(data.tasks) ? data.tasks : []);
    } catch (e) {
      toast(e.message, 'red');
      setHistOpen(false);
    } finally {
      setHistLoading(false);
    }
  };

  const canApproveTask = (tk) => {
    if (!me || !tk) return false;
    if (me.isSupervisor || me.canApprove || me.canManage) return true;
    return Number(tk.created_by) === Number(me.userId);
  };

  const personFromTask = (tk, kind) => {
    if (kind === 'creator') return (tk.creator_fio || tk.creator_username || '').trim() || null;
    if (kind === 'completed') return (tk.completed_by_fio || tk.completed_by_username || '').trim() || null;
    return (tk.assignee_fio || tk.assignee_username || '').trim() || '—';
  };

  const patchTask = async (id, body, toastMsg, toastCls) => {
    const r = await fetch(`/api/kanban/tasks/${id}`, {
      method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) {
      const err = new Error(data.error || 'Ошибка обновления');
      err.code = data.code;
      err.planMinutes = data.planMinutes;
      err.actualMinutes = data.actualMinutes;
      throw err;
    }
    setLastMoved(id);
    await loadBoard();
    if (filterKey) await reloadFilter(filterKey);
    if (toastMsg) toast(toastMsg, toastCls);
    return data;
  };

  const closeDonePrompt = () => {
    setDonePrompt(null);
    setDelayReason('');
    setDelayNote('');
    setDoneSubmitting(false);
  };

  /** Сдать задачу: если вышли за лимит — спросить причину. */
  const requestMarkDone = async (tk, toastMsg = `✅ «${tk.title}» — на проверке`, toastCls) => {
    if (!tk?.id) return;
    const preview = getDoneTimingPreview(tk);
    if (preview.needsReason) {
      setDelayReason('');
      setDelayNote('');
      setDonePrompt({ task: tk, toastMsg, toastCls, plan: preview.plan, actual: preview.actual });
      return;
    }
    try {
      await patchTask(tk.id, { status: 'done' }, toastMsg, toastCls);
    } catch (e) {
      if (e.code === 'delay_reason_required') {
        setDonePrompt({
          task: tk,
          toastMsg,
          toastCls,
          plan: e.planMinutes,
          actual: e.actualMinutes,
        });
        return;
      }
      toast(e.message, 'red');
    }
  };

  const submitDoneWithReason = async () => {
    if (!donePrompt?.task?.id) return;
    if (!delayReason) {
      toast('Выберите причину опоздания', 'red');
      return;
    }
    if (delayReason === 'other' && !delayNote.trim()) {
      toast('Для «Другое» нужен комментарий', 'red');
      return;
    }
    setDoneSubmitting(true);
    try {
      await patchTask(
        donePrompt.task.id,
        { status: 'done', delayReason, delayNote: delayNote.trim() || undefined },
        donePrompt.toastMsg,
        donePrompt.toastCls
      );
      closeDonePrompt();
    } catch (e) {
      toast(e.message, 'red');
      setDoneSubmitting(false);
    }
  };

  const createTask = async () => {
    if (!newTitle.trim() || !newAssignee) return;
    try {
      const fd = new FormData();
      if (boardId) fd.append('boardId', String(boardId));
      fd.append('title', newTitle.trim());
      if (newDescription.trim()) fd.append('description', newDescription.trim());
      fd.append('assigneeId', String(Number(newAssignee)));
      const dur = Number(newDuration);
      if (Number.isFinite(dur) && dur > 0) fd.append('durationMinutes', String(Math.round(dur)));
      newFiles.forEach((f) => fd.append('files', f));
      if (newFiles.length) {
        fd.append('originalName', JSON.stringify(newFiles.map((f) => f.name)));
      }
      const r = await fetch('/api/kanban/tasks', {
        method: 'POST',
        headers: authHeadersMultipart(),
        body: fd,
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка создания');
      setNewTitle(''); setNewDescription(''); setNewDuration(''); setNewFiles([]);
      await loadBoard();
      toast('🆕 Задача создана', 'blue');
    } catch (e) { setError(e.message); toast(e.message, 'red'); }
  };

  const findTaskById = useCallback((id) => {
    const nid = Number(id);
    if (!nid) return null;
    return (
      (board?.tasks || []).find((t) => Number(t.id) === nid)
      || (board?.approvedRecent || []).find((t) => Number(t.id) === nid)
      || filterTasks.find((t) => Number(t.id) === nid)
      || histTasks.find((t) => Number(t.id) === nid)
      || null
    );
  }, [board, filterTasks, histTasks]);

  const detailTask = detailId ? findTaskById(detailId) : null;
  const canEditDetail = !!(detailTask && me && Number(detailTask.created_by) === Number(me.userId));

  const openTaskDetail = (tk, e) => {
    if (e) {
      if (e.target.closest('button, a, label, input, select, textarea')) return;
    }
    if (!tk?.id) return;
    setDetailId(tk.id);
    setEditTitle(tk.title || '');
    setEditDesc(tk.description || '');
  };

  const closeTaskDetail = () => {
    setDetailId(null);
    setEditTitle('');
    setEditDesc('');
    setDetailSaving(false);
  };

  const saveTaskDetail = async () => {
    if (!detailTask || !canEditDetail) return;
    const title = editTitle.trim();
    if (!title) {
      toast('Укажите название', 'red');
      return;
    }
    setDetailSaving(true);
    try {
      await patchTask(detailTask.id, {
        title,
        description: editDesc.trim(),
      }, '💾 Задача обновлена', 'blue');
    } catch (e) {
      toast(e.message, 'red');
    } finally {
      setDetailSaving(false);
    }
  };

  useEffect(() => {
    if (!detailId) return;
    const live = findTaskById(detailId);
    if (!live) {
      // задача удалена
      closeTaskDetail();
      return;
    }
    // если не редактируем — подтягиваем свежие данные; при редактировании оставляем локальный черновик
    if (!canEditDetail) {
      setEditTitle(live.title || '');
      setEditDesc(live.description || '');
    }
  }, [detailId, board, filterTasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const attachFilesToTask = async (taskId, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    fd.append('originalName', JSON.stringify(files.map((f) => f.name)));
    const r = await fetch(`/api/kanban/tasks/${taskId}/files`, {
      method: 'POST',
      headers: authHeadersMultipart(),
      body: fd,
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || 'Ошибка загрузки файлов');
    await loadBoard();
    if (filterKey) await reloadFilter(filterKey);
    toast(`📎 Файлы добавлены (${files.length})`, 'blue');
  };

  const deleteTask = async (id) => {
    if (!window.confirm('Удалить задачу?')) return;
    try {
      const r = await fetch(`/api/kanban/tasks/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка');
      await loadBoard();
      toast('🗑 Задача удалена', 'red');
    } catch (e) { toast(e.message, 'red'); }
  };

  const addMember = async () => {
    if (!addUserId) return;
    try {
      const r = await fetch('/api/kanban/members', {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ userId: Number(addUserId), role: addRole, boardId: boardId || undefined }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка');
      setAddUserId('');
      await loadBoard();
      toast('👤 Участник добавлен', 'blue');
    } catch (e) { toast(e.message, 'red'); }
  };

  const removeMember = async (userId) => {
    try {
      const r = await fetch(`/api/kanban/members/${userId}?${boardQs()}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Ошибка');
      await loadBoard();
      toast('Участник убран', 'red');
    } catch (e) { toast(e.message, 'red'); }
  };

  const setLeader = async (userId) => {
    try {
      // demote current supervisors to member, promote chosen
      for (const s of board?.supervisors || []) {
        if (Number(s.user_id) !== Number(userId)) {
          await fetch(`/api/kanban/members/${s.user_id}`, {
            method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ role: 'member', boardId: boardId || undefined }),
          });
        }
      }
      await fetch(`/api/kanban/members/${userId}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ role: 'supervisor', boardId: boardId || undefined }),
      });
      await loadBoard();
      setModal(null);
      toast('👑 Руководитель назначен');
    } catch (e) { toast(e.message, 'red'); }
  };

  const switchBoard = (id) => {
    const next = Number(id) || 0;
    setBoardsMenuOpen(false);
    if (!next || next === boardId) return;
    setBoardId(next);
    setBoard(null);
    setNewAssignee('');
    setBoardUserId('');
    setHistUserId('');
    setFilterKey(null);
  };

  const submitBoardForm = async () => {
    if (!boardForm) return;
    const name = String(boardForm.name || '').trim();
    if (!name) { toast('Укажите название доски', 'red'); return; }
    setBoardSaving(true);
    try {
      const creating = boardForm.mode === 'create';
      const renameId = Number(boardForm.boardId || boardId) || 0;
      const r = await fetch(creating ? '/api/kanban/boards' : `/api/kanban/boards/${renameId}`, {
        method: creating ? 'POST' : 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ name }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось сохранить доску');
      if (creating && data.board?.id) {
        setBoardId(Number(data.board.id));
        setBoard(null);
        setNewAssignee('');
        toast('🗂 Доска создана', 'green');
      } else {
        await loadBoard();
        toast('Название доски обновлено', 'blue');
      }
      setBoardForm(null);
    } catch (e) {
      toast(e.message, 'red');
    } finally {
      setBoardSaving(false);
    }
  };

  const deleteBoard = async (target) => {
    const id = Number(target?.id || boardId) || 0;
    if (!id) return;
    if (id === 1 && !godMode) {
      toast('Основную доску можно удалить только в режиме Бога', 'red');
      return;
    }
    const name = target?.name || board?.board?.name || 'доску';
    if (!window.confirm(`Удалить доску «${name}» вместе со всеми её задачами и шаблонами?`)) return;
    try {
      const r = await fetch(`/api/kanban/boards/${id}`, { method: 'DELETE', headers: authHeaders() });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.error || 'Не удалось удалить доску');
      setBoardsMenuOpen(false);
      // Сразу убрать из локального списка (на случай задержки reload)
      setBoard((prev) => {
        if (!prev) return prev;
        const nextBoards = (Array.isArray(prev.boards) ? prev.boards : []).filter((b) => Number(b.id) !== id);
        return { ...prev, boards: nextBoards };
      });
      if (Number(boardId) === id) {
        setBoardId(0);
        setBoard(null);
      } else {
        await loadBoard();
      }
      toast(`Доска «${name}» удалена`, 'red');
    } catch (e) { toast(e.message, 'red'); }
  };

  const onCardMove = (e, cardEl) => {
    if (!finePointer || !cardEl || cardEl.classList.contains('dragging')) return;
    // без 3D-наклона — иначе кнопки внизу карточки обрезаются
    cardEl.style.transform = 'translateY(-2px)';
  };

  if (!open) return null;

  const portal = (node) => ReactDOM.createPortal(node, document.body);

  return (
    <Overlay $mobile={isMobile} $full={fullscreen} className="kanban-modal-ui">
      <Fonts />
      <Shell $light={light} className="kanban-modal-ui">
        <Blob className="b1" /><Blob className="b2" /><Blob className="b3" />
        <Particles light={light} />
        <GridOv $light={light} />

        <Topbar>
          <Brand>
            <LogoBox>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="3" width="5.5" height="18" rx="1.5" fill="#fff" opacity=".95" />
                <rect x="9.5" y="3" width="5.5" height="12" rx="1.5" fill="#fff" opacity=".75" />
                <rect x="16" y="3" width="5.5" height="8" rx="1.5" fill="#fff" opacity=".55" />
              </svg>
            </LogoBox>
            <BoardPick>
              <BoardBtn
                type="button"
                className={boardsMenuOpen ? 'open' : ''}
                onClick={() => setBoardsMenuOpen((v) => !v)}
                title="Выбрать доску"
              >
                <div className="txt">
                  <b>Канбан</b>
                  <span>{boardName}</span>
                </div>
                <span className="chev"><FiChevronDown size={14} /></span>
              </BoardBtn>
              {boardsMenuOpen && (
                <div
                  onClick={() => setBoardsMenuOpen(false)}
                  style={{ position: 'fixed', inset: 0, zIndex: 70 }}
                />
              )}
              {boardsMenuOpen && (
                <BoardMenu>
                  <h5>Доски</h5>
                  {boardsList.map((b) => {
                    const isActive = Number(b.id) === Number(boardId);
                    const canEditThis = !!(board?.canCreateBoard || me?.canManage || godMode);
                    // id=1 — системная доска: корзина только в режиме Бога
                    const canDeleteThis = canEditThis && (Number(b.id) !== 1 || godMode);
                    return (
                      <BoardRow key={b.id} $active={isActive}>
                        <BoardItem
                          type="button"
                          $active={isActive}
                          onClick={() => switchBoard(b.id)}
                        >
                          <span className="nm">{b.name}</span>
                          {isActive && <FiCheck size={14} />}
                        </BoardItem>
                        {canEditThis && (
                          <BoardDelBtn
                            type="button"
                            title={`Переименовать «${b.name}»`}
                            aria-label={`Переименовать доску ${b.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setBoardsMenuOpen(false);
                              setBoardForm({ mode: 'rename', name: b.name || '', boardId: Number(b.id) });
                            }}
                          >
                            <FiEdit2 size={15} />
                          </BoardDelBtn>
                        )}
                        {canDeleteThis && (
                          <BoardDelBtn
                            type="button"
                            $danger
                            title={`Удалить доску «${b.name}»`}
                            aria-label={`Удалить доску ${b.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteBoard(b);
                            }}
                          >
                            <FiTrash2 size={15} />
                          </BoardDelBtn>
                        )}
                      </BoardRow>
                    );
                  })}
                  {board?.canCreateBoard && <hr />}
                  {board?.canCreateBoard && (
                    <BoardItem
                      type="button"
                      onClick={() => { setBoardsMenuOpen(false); setBoardForm({ mode: 'create', name: '' }); }}
                    >
                      <FiPlus size={14} /><span className="nm">Новая доска</span>
                    </BoardItem>
                  )}
                </BoardMenu>
              )}
            </BoardPick>
          </Brand>
          <TbBtn
            className={spinning ? 'spinning' : ''}
            onClick={() => {
              setSpinning(true);
              loadBoard().then(() => toast('🔄 Доска обновлена', 'blue'));
              setTimeout(() => setSpinning(false), 600);
            }}
            disabled={loading}
          >
            <span className="spin"><FiRefreshCw /></span><span className="lbl">Обновить</span>
          </TbBtn>
          <TbBtn onClick={() => setTemplatesOpen(true)}>
            <FiCalendar /><span className="lbl">Шаблоны</span>
          </TbBtn>
          {canSeeAnalytics && (
            <TbBtn
              type="button"
              onClick={() => {
                setAnalyticsOpen(true);
              }}
            >
              <FiBarChart2 /><span className="lbl">Сводка</span>
            </TbBtn>
          )}
          {me?.canManage && (
            <TbBtn $violet onClick={() => setModal('people')}>
              👥<span className="lbl">Участники</span>
            </TbBtn>
          )}
          <Avatars>
            {members.slice(0, 4).map((m, i) => (
              <Av key={m.user_id} $bg={avColor(i)} $first={i === 0} title={displayName(m)}>
                {initials(displayName(m))}
              </Av>
            ))}
            {members.length > 4 && <Av $bg="#64748b">+{members.length - 4}</Av>}
          </Avatars>
          <TopbarEnd>
            <GodModeControl
              compact
              onChanged={() => {
                loadBoard().catch(() => {});
              }}
            />
            {!isMobile && (
              <TbBtn
                type="button"
                title={fullscreen ? 'Свернуть — показать боковое меню' : 'Развернуть на весь экран'}
                onClick={() => setFullscreen((v) => !v)}
              >
                {fullscreen ? <FiMinimize2 /> : <FiMaximize2 />}
                <span className="lbl">{fullscreen ? 'Свернуть' : 'Во весь экран'}</span>
              </TbBtn>
            )}
            <ThemeBtn $light={light} type="button" title="Тема" onClick={() => setLight((v) => !v)}>
              {light ? '🌙' : '☀️'}
            </ThemeBtn>
            <ModalCloseButton inline onClick={onClose} />
          </TopbarEnd>
        </Topbar>

        <Scroll>
          <Wrap $full={fullscreen && !isMobile}>
            {error && <Err>{error}</Err>}

            <Banner>
              <span style={{ position: 'relative', zIndex: 1 }}>
                👑 {leader ? `Руководитель: ${displayName(leader)}` : 'Руководитель не назначен'}
              </span>
              {me?.canManage && (
                <button type="button" onClick={() => setModal('leader')}>
                  {leader ? 'Сменить' : 'Назначить'}
                </button>
              )}
            </Banner>

            <Stats>
              <StatChip type="button" $sc="var(--txt)" $active={filterKey === 'all'} onClick={() => openFilter('all')}>
                <b>{visibleTasks.length}</b><span>Всего задач</span>
              </StatChip>
              <StatChip type="button" $sc="var(--amber)" $active={filterKey === 'work'} onClick={() => openFilter('work')}>
                <b>{byCol.work.length}</b><span>В работе</span>
              </StatChip>
              <StatChip type="button" $sc="var(--violet)" $active={filterKey === 'review'} onClick={() => openFilter('review')}>
                <b>{byCol.review.length}</b><span>На проверке</span>
              </StatChip>
              <StatChip type="button" $sc="var(--green)" $active={filterKey === 'approved'} onClick={() => openFilter('approved')}>
                <b>{doneToday}</b><span>Утверждено</span>
              </StatChip>
            </Stats>

            {canSeeHistory && (
              <HistoryBar>
                <h4>Фильтр и история сотрудника</h4>
                <select
                  value={boardUserId || histUserId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setBoardUserId(v);
                    setHistUserId(v);
                  }}
                >
                  <option value="">Все сотрудники на доске</option>
                  {members.map((m) => (
                    <option key={m.user_id} value={m.user_id}>{displayName(m)}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={histDate}
                  onChange={(e) => setHistDate(e.target.value)}
                />
                <CreateBtn type="button" onClick={openHistory} disabled={!(histUserId || boardUserId)}>
                  История за день
                </CreateBtn>
                {boardUserId ? (
                  <BoardFilterChip type="button" $active onClick={() => setBoardUserId('')}>
                    Сбросить фильтр доски
                  </BoardFilterChip>
                ) : (
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)' }}>
                    Выберите сотрудника — доска покажет только его задачи. «История за день» — все статусы за дату.
                  </span>
                )}
              </HistoryBar>
            )}

            <Layout>
              <section>
                <Composer $collapsed={!composerOpen}>
                  <ComposerHead
                    type="button"
                    onClick={() => setComposerOpen((v) => !v)}
                    title={composerOpen ? 'Свернуть' : 'Развернуть'}
                    aria-expanded={composerOpen}
                  >
                    <span>Новая задача{!composerOpen ? ' · нажмите, чтобы создать' : ''}</span>
                    <span className="chev">{composerOpen ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}</span>
                  </ComposerHead>
                  {composerOpen && (
                    <ComposerBody>
                      <CRow>
                        <input
                          type="text"
                          placeholder="Краткое название на доске"
                          value={newTitle}
                          onChange={(e) => setNewTitle(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && createTask()}
                        />
                        <DurationPicker
                          compact
                          light={light}
                          value={newDuration}
                          onChange={setNewDuration}
                          placeholder="Лимит времени"
                        />
                        {me?.isSupervisor || me?.canManage ? (
                          <select value={newAssignee} onChange={(e) => setNewAssignee(e.target.value)}>
                            <option value="">Исполнитель…</option>
                            {members.map((m) => (
                              <option key={m.user_id} value={m.user_id}>{displayName(m)}</option>
                            ))}
                          </select>
                        ) : (
                          <select value={newAssignee} disabled title="Вам доступны только свои задачи">
                            <option value={me?.userId || ''}>
                              {members.find((m) => Number(m.user_id) === Number(me?.userId))
                                ? displayName(members.find((m) => Number(m.user_id) === Number(me?.userId)))
                                : 'Я'}
                            </option>
                          </select>
                        )}
                        <CreateBtn type="button" onClick={createTask} disabled={!members.length}>
                          <FiPlus /> Создать
                        </CreateBtn>
                      </CRow>
                      <ComposerDesc
                        placeholder="Подробности задачи (необязательно) — будут внутри задачи"
                        value={newDescription}
                        onChange={(e) => setNewDescription(e.target.value)}
                      />
                      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                        <FilePick>
                          <FiPaperclip /> Прикрепить файл
                          <input
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,.xls,.xlsx,.pdf,.doc,.docx,.zip,.rar,image/jpeg,image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip,application/x-zip-compressed,application/vnd.rar,application/x-rar-compressed"
                            onChange={(e) => {
                              const picked = Array.from(e.target.files || []);
                              setNewFiles((prev) => [...prev, ...picked].slice(0, 8));
                              e.target.value = '';
                            }}
                          />
                        </FilePick>
                        <span style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 700 }}>
                          jpg, png, pdf, doc, xls, zip, rar · до 8 файлов
                        </span>
                      </div>
                      {!!newFiles.length && (
                        <FileListMini>
                          {newFiles.map((f, i) => (
                            <span key={`${f.name}-${i}`}>
                              {f.name}
                              <button type="button" style={{ marginLeft: 6 }} onClick={() => setNewFiles((prev) => prev.filter((_, j) => j !== i))}>×</button>
                            </span>
                          ))}
                        </FileListMini>
                      )}
                      {!members.length && (
                        <div style={{ marginTop: 10, color: 'var(--mut)', fontSize: 12, fontWeight: 700 }}>
                          Сначала добавьте участников (кнопка «Участники»).
                        </div>
                      )}
                    </ComposerBody>
                  )}
                </Composer>

                <Board
                  onDragOver={(e) => {
                    e.preventDefault();
                    const col = e.target.closest('[data-col]');
                    setOverCol(col?.dataset?.col || null);
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    const col = e.target.closest('[data-col]');
                    setOverCol(null);
                    if (!col || dragId == null) return;
                    const meta = COLS.find((c) => c.k === col.dataset.col);
                    const tk = tasks.find((t) => Number(t.id) === Number(dragId));
                    if (!meta || !tk || tk.status === meta.api) return;
                    try {
                      if (meta.api === 'done') {
                        await requestMarkDone(tk, `📦 «${tk.title}» → ${meta.t}`, 'blue');
                      } else {
                        await patchTask(dragId, { status: meta.api }, `📦 «${tk.title}» → ${meta.t}`, 'blue');
                      }
                    } catch (err) { toast(err.message, 'red'); }
                    setDragId(null);
                  }}
                >
                  {COLS.map((c) => {
                    const list = byCol[c.k] || [];
                    return (
                      <Col key={c.k} $kind={c.k} $over={overCol === c.k} data-col={c.k}>
                        <ColHead>
                          <span className="dot" />
                          {c.t}
                          <span className="cnt">{list.length}</span>
                        </ColHead>
                        <ColBody>
                          {list.map((tk) => {
                            const idx = memberIndex[Number(tk.assignee_id)] ?? 0;
                            const name = personFromTask(tk, 'assignee');
                            const creator = personFromTask(tk, 'creator');
                            const canAct = me?.isSupervisor || Number(tk.assignee_id) === Number(me?.userId);
                            const filesCount = Array.isArray(tk.attachments) ? tk.attachments.length : 0;
                            const openLabel = [
                              'Открыть',
                              tk.description ? 'описание' : null,
                              filesCount ? `файлы (${filesCount})` : null,
                            ].filter(Boolean).join(' · ');
                            return (
                              <Card
                                key={tk.id}
                                draggable
                                data-id={tk.id}
                                $justMoved={lastMoved === tk.id}
                                $dragging={dragId === tk.id}
                                onClick={(e) => openTaskDetail(tk, e)}
                                onDragStart={(e) => {
                                  setDragId(tk.id);
                                  e.currentTarget.style.transform = '';
                                  e.dataTransfer.effectAllowed = 'move';
                                }}
                                onDragEnd={() => { setDragId(null); setOverCol(null); }}
                                onMouseMove={(e) => onCardMove(e, e.currentTarget)}
                                onMouseLeave={(e) => { if (dragId !== tk.id) e.currentTarget.style.transform = ''; }}
                              >
                                <div className="tt">{tk.title}</div>
                                <OpenHint>{openLabel}</OpenHint>
                                {creator && (
                                  <div className="from">От: {creator}</div>
                                )}
                                <div className="meta">
                                  <span className="who">
                                    <Av $sm $bg={avColor(idx)}>{initials(name)}</Av>
                                    {name}
                                  </span>
                                  {tk.time_hint && (
                                    <span className={`time${isLate(tk) ? ' late' : ''}`}>🕐 {tk.time_hint}</span>
                                  )}
                                  <TaskTimerBadge tk={tk} nowMs={nowTick} />
                                  <TaskTimingResultBadge tk={tk} />
                                </div>
                                <div className="acts" onClick={(e) => e.stopPropagation()}>
                                  {c.k === 'new' && canAct && (
                                    <ActBtn $kind="toWork" type="button" onClick={() => patchTask(tk.id, { status: 'in_progress' }, `🔧 «${tk.title}» — в работе`, 'blue').catch((e) => toast(e.message, 'red'))}>
                                      🔧 В работу
                                    </ActBtn>
                                  )}
                                  {c.k === 'work' && canAct && (
                                    <ActBtn $kind="toDone" type="button" onClick={() => requestMarkDone(tk)}>
                                      ✓ Готово
                                    </ActBtn>
                                  )}
                                  {c.k === 'review' && canApproveTask(tk) && (
                                    <>
                                      <ActBtn
                                        $kind="ret"
                                        type="button"
                                        onClick={() => patchTask(tk.id, { status: 'in_progress' }, '↩ Возвращена в работу', 'red').catch((e) => toast(e.message, 'red'))}
                                      >
                                        <FiRotateCcw /> Вернуть
                                      </ActBtn>
                                      <ActBtn
                                        $kind="ok"
                                        type="button"
                                        onClick={async (e) => {
                                          const r = e.currentTarget.getBoundingClientRect();
                                          confetti(r.left + r.width / 2, r.top + r.height / 2);
                                          try {
                                            await patchTask(tk.id, { status: 'approved' }, `🎉 Утверждено: «${tk.title}»`);
                                            setBump(true);
                                            setTimeout(() => setBump(false), 500);
                                          } catch (err) { toast(err.message, 'red'); }
                                        }}
                                      >
                                        <FiCheck /> Утвердить
                                      </ActBtn>
                                    </>
                                  )}
                                  {(me?.isSupervisor || Number(tk.created_by) === Number(me?.userId) || Number(tk.assignee_id) === Number(me?.userId)) && (
                                    <>
                                      <ActBtn $kind="del" type="button" onClick={() => deleteTask(tk.id)}><FiTrash2 /></ActBtn>
                                      <FilePick style={{ padding: '7px 10px', borderRadius: 10 }}>
                                        <FiPaperclip />
                                        <input
                                          type="file"
                                          multiple
                                          accept=".jpg,.jpeg,.png,.xls,.xlsx,.pdf,.doc,.docx,.zip,.rar"
                                          onChange={(e) => {
                                            attachFilesToTask(tk.id, e.target.files).catch((err) => toast(err.message, 'red'));
                                            e.target.value = '';
                                          }}
                                        />
                                      </FilePick>
                                    </>
                                  )}
                                </div>
                              </Card>
                            );
                          })}
                          {!list.length && <Empty>Перетащи задачу сюда ✨</Empty>}
                        </ColBody>
                      </Col>
                    );
                  })}
                </Board>
              </section>

              <Review>
                <h3>На проверке у руководителя</h3>
                <div className="sub">
                  Задачи ждут утверждения · <DoneChip $bump={bump}>✓ {doneToday}</DoneChip>
                </div>
                {byCol.review.map((tk) => {
                  const creator = personFromTask(tk, 'creator');
                  const filesCount = Array.isArray(tk.attachments) ? tk.attachments.length : 0;
                  const openLabel = [
                    'Открыть',
                    tk.description ? 'описание' : null,
                    filesCount ? `файлы (${filesCount})` : null,
                  ].filter(Boolean).join(' · ');
                  return (
                    <RCard key={`r-${tk.id}`} onClick={(e) => openTaskDetail(tk, e)} style={{ cursor: 'pointer' }}>
                      <div className="tt">
                        {tk.time_hint ? <b style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tk.time_hint}</b> : null}
                        {tk.time_hint ? ' · ' : ''}
                        {tk.title}
                      </div>
                      <OpenHint style={{ paddingLeft: 0 }}>{openLabel}</OpenHint>
                      <div className="who">Исполнитель: {personFromTask(tk, 'assignee')}</div>
                      {creator && <div className="from">Назначил(а): {creator}</div>}
                      <div style={{ marginTop: 6 }}><TaskTimingResultBadge tk={tk} /></div>
                      {canApproveTask(tk) ? (
                        <div className="acts" onClick={(e) => e.stopPropagation()}>
                          <ActBtn
                            $kind="ret"
                            type="button"
                            onClick={() => patchTask(tk.id, { status: 'in_progress' }, '↩ Возвращена в работу', 'red').catch((e) => toast(e.message, 'red'))}
                          >
                            <FiRotateCcw /> Вернуть
                          </ActBtn>
                          <ActBtn
                            $kind="ok"
                            type="button"
                            onClick={async (e) => {
                              const r = e.currentTarget.getBoundingClientRect();
                              confetti(r.left + r.width / 2, r.top + r.height / 2);
                              try {
                                await patchTask(tk.id, { status: 'approved' }, `🎉 Утверждено: «${tk.title}»`);
                                setBump(true);
                                setTimeout(() => setBump(false), 500);
                              } catch (err) { toast(err.message, 'red'); }
                            }}
                          >
                            <FiCheck /> Утвердить
                          </ActBtn>
                        </div>
                      ) : (
                        <div className="who">Ожидает решения руководителя</div>
                      )}
                    </RCard>
                  );
                })}
                {!byCol.review.length && (
                  <Empty style={{ '--cc': 'var(--violet)' }}>
                    Пока пусто — задачи появятся<br />после «Готово» ✅
                  </Empty>
                )}

                {!!approvedRecent.length && (
                  <>
                    <h3 style={{ marginTop: 18 }}>Утверждено</h3>
                    <div className="sub">Недавно подтверждённые задачи</div>
                    {approvedRecent
                      .filter((tk) => !boardUserId || Number(tk.assignee_id) === Number(boardUserId))
                      .slice(0, 12)
                      .map((tk) => (
                      <RCard key={`a-${tk.id}`} style={{ borderLeftColor: 'var(--green)', cursor: 'pointer' }} onClick={(e) => openTaskDetail(tk, e)}>
                        <div className="okmark"><FiCheck /> Утверждено</div>
                        <div className="tt">{tk.title}</div>
                        <div className="who">Исполнитель: {personFromTask(tk, 'assignee')}</div>
                        {personFromTask(tk, 'creator') && (
                          <div className="from">Назначил(а): {personFromTask(tk, 'creator')}</div>
                        )}
                      </RCard>
                    ))}
                  </>
                )}
              </Review>
            </Layout>
          </Wrap>
        </Scroll>

        {toasts.length > 0 && portal(
          <ToastHost>
            {toasts.map((t) => <Toast key={t.id} $cls={t.cls}>{t.m}</Toast>)}
          </ToastHost>
        )}

        {detailTask && portal(
          <ModalBack
            className="kanban-modal-ui"
            onClick={closeTaskDetail}
            style={{ padding: isMobile || (typeof window !== 'undefined' && window.innerWidth <= 900) ? 0 : 16, alignItems: isMobile || (typeof window !== 'undefined' && window.innerWidth <= 900) ? 'stretch' : 'center', display: 'flex', justifyContent: 'center' }}
          >
            <TaskDetailShell
              onClick={(e) => e.stopPropagation()}
              style={{
                ['--solid']: light ? '#fff' : '#101828',
                ['--txt']: light ? '#44566e' : '#eaf2fb',
                ['--mut']: light ? '#6b7c93' : '#8b96ab',
                ['--head']: light ? '#556780' : '#eaf2fb',
                ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
                ['--panel2']: light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)',
                ['--blue']: '#38b6ff',
                ['--amber']: '#fbbf24',
                ['--violet']: '#8b5cf6',
                ['--green']: '#2dd4a0',
                ['--red']: '#fb7185',
              }}
            >
              <FilterHead>
                <h3>Задача</h3>
                <ModalCloseButton inline onClick={closeTaskDetail} />
              </FilterHead>
              <TaskDetailBody>
                {canEditDetail ? (
                  <TitleInput
                    value={editTitle}
                    onChange={(e) => setEditTitle(e.target.value)}
                    placeholder="Краткое название"
                  />
                ) : (
                  <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.35 }}>{detailTask.title}</div>
                )}

                <MetaLine>
                  <span>Статус: {STATUS_LABEL[detailTask.status] || detailTask.status}</span>
                  {detailTask.time_hint ? <span>Время: {detailTask.time_hint}</span> : null}
                  {Number(detailTask.duration_minutes) > 0 ? <span>Лимит: {formatDurationLabel(detailTask.duration_minutes)}</span> : null}
                  {detailTask.status === 'in_progress' && Number(detailTask.duration_minutes) > 0 && detailTask.started_at ? (
                    <span>
                      Таймер:{' '}
                      <TaskTimerBadge tk={detailTask} nowMs={nowTick} />
                    </span>
                  ) : null}
                  {(detailTask.status === 'done' || detailTask.status === 'approved') && (
                    <>
                      {detailTask.actual_minutes != null && (
                        <span>
                          План: {Number(detailTask.duration_minutes) > 0 ? formatDurationLabel(detailTask.duration_minutes) : '—'} · Факт: {detailTask.actual_minutes} мин
                          {Number(detailTask.duration_minutes) > 0
                            ? ` (${formatTimingDelta(Number(detailTask.duration_minutes), Number(detailTask.actual_minutes))})`
                            : ''}
                        </span>
                      )}
                      {detailTask.timing_result && (
                        <span>Результат: {TIMING_LABEL[detailTask.timing_result] || detailTask.timing_result}</span>
                      )}
                      {detailTask.delay_reason && (
                        <span>
                          Причина: {DELAY_REASONS.find((r) => r.id === detailTask.delay_reason)?.label || detailTask.delay_reason}
                          {detailTask.delay_note ? ` — ${detailTask.delay_note}` : ''}
                        </span>
                      )}
                    </>
                  )}
                  <span>Исполнитель: {personFromTask(detailTask, 'assignee')}</span>
                  {personFromTask(detailTask, 'creator') && <span>От: {personFromTask(detailTask, 'creator')}</span>}
                  {personFromTask(detailTask, 'completed') && <span>Выполнил(а): {personFromTask(detailTask, 'completed')}</span>}
                </MetaLine>

                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)', letterSpacing: '.4px', textTransform: 'uppercase' }}>
                  Подробности
                </div>
                {canEditDetail ? (
                  <DescArea
                    value={editDesc}
                    onChange={(e) => setEditDesc(e.target.value)}
                    placeholder="Описание задачи…"
                  />
                ) : (
                  <DescBox>
                    {detailTask.description
                      ? detailTask.description
                      : 'Описание не указано'}
                  </DescBox>
                )}

                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)', letterSpacing: '.4px', textTransform: 'uppercase' }}>
                  Файлы
                </div>
                {Array.isArray(detailTask.attachments) && detailTask.attachments.length ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {detailTask.attachments.map((f) => (
                      <FileChip key={f.filename || f.url} href={f.url} target="_blank" rel="noreferrer" download>
                        <FiPaperclip /><span>{displayFileName(f.originalname || f.name || f.filename)}</span>
                      </FileChip>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mut)' }}>Файлов пока нет</div>
                )}

                {canEditDetail && (
                  <FilePick>
                    <FiPaperclip /> Добавить файлы
                    <input
                      type="file"
                      multiple
                      accept=".jpg,.jpeg,.png,.xls,.xlsx,.pdf,.doc,.docx,.zip,.rar"
                      onChange={(e) => {
                        attachFilesToTask(detailTask.id, e.target.files).catch((err) => toast(err.message, 'red'));
                        e.target.value = '';
                      }}
                    />
                  </FilePick>
                )}

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
                  {canEditDetail && (
                    <CreateBtn type="button" onClick={saveTaskDetail} disabled={detailSaving}>
                      {detailSaving ? 'Сохранение…' : 'Сохранить'}
                    </CreateBtn>
                  )}
                  <CreateBtn
                    type="button"
                    style={{ background: 'var(--panel2)', color: 'var(--txt)', boxShadow: 'none', border: '1px solid var(--stroke)' }}
                    onClick={closeTaskDetail}
                  >
                    Закрыть
                  </CreateBtn>
                </div>
                {!canEditDetail && (
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)' }}>
                    Редактировать может только автор задачи. Вы можете читать описание и скачивать файлы.
                  </div>
                )}
              </TaskDetailBody>
            </TaskDetailShell>
          </ModalBack>
        )}

        {histOpen && portal(
          <ModalBack
            className="kanban-modal-ui"
            onClick={() => setHistOpen(false)}
            style={{ padding: isMobile || (typeof window !== 'undefined' && window.innerWidth <= 900) ? 0 : 16 }}
          >
            <FilterShell
              onClick={(e) => e.stopPropagation()}
              style={{
                ['--solid']: light ? '#fff' : '#101828',
                ['--txt']: light ? '#44566e' : '#eaf2fb',
                ['--mut']: light ? '#6b7c93' : '#8b96ab',
                ['--head']: light ? '#556780' : '#eaf2fb',
                ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
                ['--panel2']: light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)',
                ['--blue']: '#38b6ff',
                ['--green']: '#2dd4a0',
                ['--amber']: '#fbbf24',
                ['--violet']: '#8b5cf6',
                ['--red']: '#fb7185',
              }}
            >
              <FilterHead>
                <h3>
                  История · {members.find((m) => String(m.user_id) === String(histUserId || boardUserId)) ? displayName(members.find((m) => String(m.user_id) === String(histUserId || boardUserId))) : 'сотрудник'}
                  {' · '}{histDate}
                  {!histLoading ? ` · ${histTasks.length}` : ''}
                </h3>
                <ModalCloseButton inline onClick={() => setHistOpen(false)} />
              </FilterHead>
              <FilterBody>
                {histLoading && (
                  <div style={{ color: 'var(--mut)', fontWeight: 700, padding: '40px 0', textAlign: 'center' }}>Загрузка…</div>
                )}
                {!histLoading && !histTasks.length && (
                  <div style={{ color: 'var(--mut)', fontWeight: 700, padding: '40px 12px', textAlign: 'center', border: '2px dashed var(--stroke)', borderRadius: 14 }}>
                    За этот день задач не найдено
                  </div>
                )}
                {!histLoading && !!histTasks.length && (
                  <FilterList>
                    {histTasks.map((tk) => {
                      const filesCount = Array.isArray(tk.attachments) ? tk.attachments.length : 0;
                      const openLabel = [
                        'Открыть',
                        tk.description ? 'описание' : null,
                        filesCount ? `файлы (${filesCount})` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <FilterCard
                          key={`h-${tk.id}`}
                          $accent={
                            tk.status === 'approved' ? 'var(--green)'
                              : tk.status === 'done' ? 'var(--violet)'
                                : tk.status === 'in_progress' ? 'var(--amber)'
                                  : 'var(--blue)'
                          }
                          onClick={(e) => openTaskDetail(tk, e)}
                          style={{ cursor: 'pointer' }}
                        >
                          <div className="tt">{tk.title}</div>
                          <OpenHint style={{ paddingLeft: 0 }}>{openLabel}</OpenHint>
                          <div className="meta">
                            <span className="badge">{STATUS_LABEL[tk.status] || tk.status}</span>
                            {tk.time_hint ? <span>🕐 {tk.time_hint}</span> : null}
                            <TaskTimerBadge tk={tk} nowMs={nowTick} />
                            <TaskTimingResultBadge tk={tk} />
                            {personFromTask(tk, 'creator') ? <span>От: {personFromTask(tk, 'creator')}</span> : null}
                          </div>
                        </FilterCard>
                      );
                    })}
                  </FilterList>
                )}
              </FilterBody>
              <FilterNav>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)' }}>
                  Нажмите задачу, чтобы открыть описание и файлы
                </div>
                <NavBtn type="button" onClick={() => setHistOpen(false)}>Закрыть</NavBtn>
              </FilterNav>
            </FilterShell>
          </ModalBack>
        )}

        {filterKey && portal(
          <ModalBack
            className="kanban-modal-ui"
            onClick={() => setFilterKey(null)}
            style={{ padding: isMobile || window.innerWidth <= 900 ? 0 : 16, alignItems: isMobile || window.innerWidth <= 900 ? 'stretch' : 'center' }}
          >
            <FilterShell
              onClick={(e) => e.stopPropagation()}
              onTouchStart={onFilterTouchStart}
              onTouchEnd={onFilterTouchEnd}
              style={{
                ['--solid']: light ? '#fff' : '#101828',
                ['--txt']: light ? '#44566e' : '#eaf2fb',
                ['--mut']: light ? '#6b7c93' : '#8b96ab',
                ['--head']: light ? '#556780' : '#eaf2fb',
                ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
                ['--panel2']: light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)',
                ['--panel']: light ? 'rgba(255,255,255,.95)' : 'rgba(15,21,36,.62)',
                ['--blue']: '#38b6ff',
                ['--amber']: '#fbbf24',
                ['--violet']: '#8b5cf6',
                ['--green']: '#2dd4a0',
                ['--red']: '#fb7185',
              }}
            >
              <FilterHead>
                <h3>
                  {FILTER_META[filterKey]?.title || 'Задачи'}
                  {!filterLoading ? ` · ${filterTasks.length}` : ''}
                </h3>
                <ModalCloseButton inline onClick={() => setFilterKey(null)} />
              </FilterHead>

              <FilterTabs>
                {FILTER_ORDER.map((key) => {
                  const meta = FILTER_META[key];
                  const count =
                    key === 'all' ? visibleTasks.length
                      : key === 'work' ? byCol.work.length
                        : key === 'review' ? byCol.review.length
                          : doneToday;
                  const bg =
                    key === 'work' ? 'linear-gradient(135deg,#fbbf24,#f59e0b)'
                      : key === 'review' ? 'linear-gradient(135deg,#a78bfa,#7c3aed)'
                        : key === 'approved' ? 'linear-gradient(135deg,#34e3b0,#14a87c)'
                          : 'linear-gradient(135deg,#38b6ff,#2488d8)';
                  return (
                    <FilterTab
                      key={key}
                      type="button"
                      $active={filterKey === key}
                      $bg={bg}
                      onClick={() => openFilter(key)}
                    >
                      {meta.short} · {count}
                    </FilterTab>
                  );
                })}
              </FilterTabs>

              <FilterBody>
                {filterLoading && (
                  <div style={{ color: 'var(--mut)', fontWeight: 700, padding: '40px 0', textAlign: 'center' }}>Загрузка…</div>
                )}
                {!filterLoading && !filterTasks.length && (
                  <div style={{ color: 'var(--mut)', fontWeight: 700, padding: '40px 12px', textAlign: 'center', border: '2px dashed var(--stroke)', borderRadius: 14 }}>
                    В этой категории пока нет задач
                  </div>
                )}
                {!filterLoading && !!filterTasks.length && (
                  <FilterList>
                    {filterTasks.map((tk) => {
                      const idx = memberIndex[Number(tk.assignee_id)] ?? 0;
                      const name = personFromTask(tk, 'assignee');
                      const creator = personFromTask(tk, 'creator');
                      const canAct = me?.isSupervisor || Number(tk.assignee_id) === Number(me?.userId);
                      const canDelete = me?.isSupervisor || Number(tk.created_by) === Number(me?.userId) || Number(tk.assignee_id) === Number(me?.userId);
                      const filesCount = Array.isArray(tk.attachments) ? tk.attachments.length : 0;
                      const openLabel = [
                        'Открыть',
                        tk.description ? 'описание' : null,
                        filesCount ? `файлы (${filesCount})` : null,
                      ].filter(Boolean).join(' · ');
                      return (
                        <FilterCard key={tk.id} $accent={FILTER_META[filterKey]?.accent} onClick={(e) => openTaskDetail(tk, e)} style={{ cursor: 'pointer' }}>
                          <div className="tt">
                            {tk.time_hint ? <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{tk.time_hint} · </span> : null}
                            {Number(tk.duration_minutes) > 0 ? <span>⏱ {formatDurationLabel(tk.duration_minutes)} · </span> : null}
                            {tk.title}
                          </div>
                          <OpenHint style={{ paddingLeft: 0 }}>{openLabel}</OpenHint>
                          {creator && (
                            <div style={{ fontSize: 11, color: 'var(--mut)', fontWeight: 700, marginBottom: 6 }}>От: {creator}</div>
                          )}
                          <div className="meta">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
                              <Av $sm $bg={avColor(idx)}>{initials(name)}</Av>
                              {name}
                            </span>
                            <span className="badge">{STATUS_LABEL[tk.status] || tk.status}</span>
                            <TaskTimerBadge tk={tk} nowMs={nowTick} />
                            <TaskTimingResultBadge tk={tk} />
                          </div>
                          <div className="acts" onClick={(e) => e.stopPropagation()}>
                            {tk.status === 'new' && canAct && (
                              <ActBtn
                                $kind="toWork"
                                type="button"
                                onClick={() => runFilterAction(() => patchTask(tk.id, { status: 'in_progress' }, `🔧 «${tk.title}» — в работе`, 'blue'))}
                              >
                                🔧 В работу
                              </ActBtn>
                            )}
                            {tk.status === 'in_progress' && canAct && (
                              <ActBtn
                                $kind="toDone"
                                type="button"
                                onClick={() => runFilterAction(() => requestMarkDone(tk))}
                              >
                                ✓ Готово
                              </ActBtn>
                            )}
                            {tk.status === 'done' && canApproveTask(tk) && (
                              <>
                                <ActBtn
                                  $kind="ret"
                                  type="button"
                                  onClick={() => runFilterAction(() => patchTask(tk.id, { status: 'in_progress' }, '↩ Возвращена в работу', 'red'))}
                                >
                                  <FiRotateCcw /> Вернуть
                                </ActBtn>
                                <ActBtn
                                  $kind="ok"
                                  type="button"
                                  onClick={(e) => {
                                    const r = e.currentTarget.getBoundingClientRect();
                                    confetti(r.left + r.width / 2, r.top + r.height / 2);
                                    runFilterAction(async () => {
                                      await patchTask(tk.id, { status: 'approved' }, `🎉 Утверждено: «${tk.title}»`);
                                      setBump(true);
                                      setTimeout(() => setBump(false), 500);
                                    });
                                  }}
                                >
                                  <FiCheck /> Утвердить
                                </ActBtn>
                              </>
                            )}
                            {canDelete && (
                              <>
                                <ActBtn
                                  $kind="del"
                                  type="button"
                                  onClick={() => runFilterAction(async () => {
                                    if (!window.confirm('Удалить задачу?')) return;
                                    const r = await fetch(`/api/kanban/tasks/${tk.id}`, { method: 'DELETE', headers: authHeaders() });
                                    const data = await r.json().catch(() => ({}));
                                    if (!r.ok) throw new Error(data.error || 'Ошибка');
                                    toast('🗑 Задача удалена', 'red');
                                    await loadBoard();
                                  })}
                                >
                                  <FiTrash2 />
                                </ActBtn>
                                <FilePick style={{ padding: '7px 10px', borderRadius: 10 }}>
                                  <FiPaperclip />
                                  <input
                                    type="file"
                                    multiple
                                    accept=".jpg,.jpeg,.png,.xls,.xlsx,.pdf,.doc,.docx,.zip,.rar"
                                    onChange={(e) => {
                                      runFilterAction(() => attachFilesToTask(tk.id, e.target.files));
                                      e.target.value = '';
                                    }}
                                  />
                                </FilePick>
                              </>
                            )}
                          </div>
                        </FilterCard>
                      );
                    })}
                  </FilterList>
                )}
              </FilterBody>

              <FilterNav>
                <NavBtn type="button" disabled={filterIdx <= 0} onClick={() => goFilter(-1)}>
                  <FiChevronLeft /> Назад
                </NavBtn>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)' }}>
                  {filterIdx + 1} / {FILTER_ORDER.length} · свайп
                </div>
                <NavBtn type="button" disabled={filterIdx >= FILTER_ORDER.length - 1} onClick={() => goFilter(1)}>
                  Далее <FiChevronRight />
                </NavBtn>
              </FilterNav>
            </FilterShell>
          </ModalBack>
        )}

        {modal && portal(
          <ModalBack className="kanban-modal-ui" onClick={() => setModal(null)}>
            <MBox
              onClick={(e) => e.stopPropagation()}
              style={{
                ['--solid']: light ? '#fff' : '#101828',
                ['--txt']: light ? '#44566e' : '#eaf2fb',
                ['--mut']: light ? '#6b7c93' : '#8b96ab',
                ['--head']: light ? '#556780' : '#eaf2fb',
                ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
                ['--panel2']: light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)',
                ['--blue']: '#38b6ff',
                ['--red']: '#fb7185',
                ['--grad']: 'linear-gradient(135deg,#38b6ff,#8b5cf6)',
              }}
            >
              <h3>
                <span>{modal === 'leader' ? 'Назначить руководителя' : 'Участники отдела'}</span>
                <button type="button" onClick={() => setModal(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit' }}>
                  <FiX size={18} />
                </button>
              </h3>
              {modal === 'leader' && members.map((m, i) => (
                <PRow key={m.user_id}>
                  <Av $bg={avColor(i)}>{initials(displayName(m))}</Av>
                  <b>{displayName(m)}</b>
                  <PickBtn type="button" onClick={() => setLeader(m.user_id)}>Выбрать</PickBtn>
                </PRow>
              ))}
              {modal === 'people' && (
                <>
                  {members.map((m, i) => (
                    <PRow key={m.user_id}>
                      <Av $bg={avColor(i)}>{initials(displayName(m))}</Av>
                      <b>{displayName(m)} · {m.member_role === 'supervisor' ? 'рук.' : 'сотрудник'}</b>
                      <RmBtn type="button" onClick={() => removeMember(m.user_id)} title="Убрать">✕</RmBtn>
                    </PRow>
                  ))}
                  <MAdd>
                    <select value={addUserId} onChange={(e) => setAddUserId(e.target.value)}>
                      <option value="">Сотрудник…</option>
                      {users.filter((u) => !memberIds.has(Number(u.id))).map((u) => (
                        <option key={u.id} value={u.id}>{(u.fio || u.username) + (u.department ? ` · ${u.department}` : '')}</option>
                      ))}
                    </select>
                    <select value={addRole} onChange={(e) => setAddRole(e.target.value)}>
                      <option value="member">Сотрудник</option>
                      <option value="supervisor">Руководитель</option>
                    </select>
                    <button type="button" onClick={addMember}>Добавить</button>
                  </MAdd>
                </>
              )}
              {modal === 'leader' && !members.length && (
                <div style={{ color: 'var(--mut)', fontWeight: 700 }}>Сначала добавьте участников</div>
              )}
            </MBox>
          </ModalBack>
        )}

        {templatesOpen && (
          <KanbanTemplatesPanel
            light={light}
            members={members}
            me={me}
            canSeeAll={!!(me?.isSupervisor || me?.canManage)}
            boardId={boardId || null}
            toast={toast}
            onClose={() => setTemplatesOpen(false)}
          />
        )}

        {analyticsOpen && canSeeAnalytics && portal(
          <ModalBack
            className="kanban-modal-ui"
            onClick={() => setAnalyticsOpen(false)}
            style={{ padding: isMobile || window.innerWidth <= 900 ? 0 : 16, alignItems: isMobile || window.innerWidth <= 900 ? 'stretch' : 'center' }}
          >
            <FilterShell
              onClick={(e) => e.stopPropagation()}
              style={{
                width: 'min(820px, 100%)',
                ['--solid']: light ? '#fff' : '#101828',
                ['--txt']: light ? '#44566e' : '#eaf2fb',
                ['--mut']: light ? '#6b7c93' : '#8b96ab',
                ['--head']: light ? '#556780' : '#eaf2fb',
                ['--stroke']: light ? 'rgba(23,35,59,.1)' : 'rgba(255,255,255,.09)',
                ['--panel2']: light ? 'rgba(248,250,252,.95)' : 'rgba(255,255,255,.04)',
                ['--panel']: light ? 'rgba(255,255,255,.95)' : 'rgba(15,21,36,.62)',
                ['--blue']: '#38b6ff',
                ['--amber']: '#fbbf24',
                ['--violet']: '#8b5cf6',
                ['--green']: '#2dd4a0',
                ['--red']: '#fb7185',
              }}
            >
              <FilterHead>
                <h3>Сводка по отделу</h3>
                <ModalCloseButton inline onClick={() => setAnalyticsOpen(false)} />
              </FilterHead>

              <div style={{ padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: 10, borderBottom: '1px solid var(--stroke)', flexShrink: 0 }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[
                    { id: 'week', label: '7 дней' },
                    { id: 'month', label: 'Месяц' },
                  ].map((r) => (
                    <FilterTab
                      key={r.id}
                      type="button"
                      $active={analyticsRange === r.id}
                      $bg="linear-gradient(135deg,#38b6ff,#8b5cf6)"
                      onClick={() => setAnalyticsRange(r.id)}
                    >
                      {r.label}
                    </FilterTab>
                  ))}
                </div>
                <select
                  value={analyticsAssignee}
                  onChange={(e) => setAnalyticsAssignee(e.target.value)}
                  style={{
                    marginLeft: 'auto',
                    padding: '8px 12px',
                    borderRadius: 12,
                    border: '1px solid var(--stroke)',
                    background: 'var(--panel2)',
                    color: 'var(--txt)',
                    fontWeight: 700,
                    fontSize: 12.5,
                    minWidth: 180,
                  }}
                >
                  <option value="">Все сотрудники</option>
                  {(board?.workers || []).map((m) => (
                    <option key={m.user_id} value={m.user_id}>{displayName(m)}</option>
                  ))}
                  {(board?.supervisors || []).map((m) => (
                    <option key={`s-${m.user_id}`} value={m.user_id}>{displayName(m)} (рук.)</option>
                  ))}
                </select>
                <TbBtn type="button" onClick={loadAnalytics} disabled={analyticsLoading} style={{ padding: '8px 12px' }}>
                  <span className="spin"><FiRefreshCw /></span>
                  <span className="lbl">Обновить</span>
                </TbBtn>
              </div>

              <FilterBody style={{ padding: 14 }}>
                {analyticsLoading && (
                  <div style={{ color: 'var(--mut)', fontWeight: 700, textAlign: 'center', padding: 24 }}>Загрузка…</div>
                )}
                {!analyticsLoading && analyticsError && (
                  <Err style={{ margin: 0 }}>{analyticsError}</Err>
                )}
                {!analyticsLoading && analyticsData && (
                  <>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--mut)', marginBottom: 12 }}>
                      Период: {analyticsData.period?.from} — {analyticsData.period?.to}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                      {[
                        { l: 'Сдано', v: analyticsData.totals?.done ?? 0, c: 'var(--blue)' },
                        { l: 'В срок / раньше', v: `${analyticsData.totals?.onTimePct ?? '—'}%`, c: 'var(--green)' },
                        { l: 'С опозданием', v: `${analyticsData.totals?.latePct ?? '—'}%`, c: 'var(--red)' },
                        { l: 'Ср. план', v: analyticsData.totals?.avgPlanMin != null ? `${analyticsData.totals.avgPlanMin} мин` : '—', c: 'var(--amber)' },
                        { l: 'Ср. факт', v: analyticsData.totals?.avgActualMin != null ? `${analyticsData.totals.avgActualMin} мин` : '—', c: 'var(--violet)' },
                      ].map((s) => (
                        <div
                          key={s.l}
                          style={{
                            padding: '12px 14px',
                            borderRadius: 14,
                            border: '1px solid var(--stroke)',
                            background: 'var(--panel2)',
                          }}
                        >
                          <div style={{ fontSize: 10.5, fontWeight: 800, color: 'var(--mut)', letterSpacing: '.4px', textTransform: 'uppercase' }}>{s.l}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4, color: s.c, fontFamily: "'JetBrains Mono', monospace" }}>{s.v}</div>
                        </div>
                      ))}
                    </div>

                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)', letterSpacing: '.4px', textTransform: 'uppercase', marginBottom: 8 }}>
                      По сотрудникам
                    </div>
                    {!(analyticsData.byAssignee || []).length ? (
                      <div style={{ color: 'var(--mut)', fontWeight: 700, padding: '16px 0' }}>
                        Нет сданных задач за период (нужны задачи со статусом «Готово» / «Утверждено» и зафиксированным временем).
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
                        {analyticsData.byAssignee.map((row, idx) => (
                          <div
                            key={row.userId || idx}
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 10,
                              alignItems: 'center',
                              padding: '12px 14px',
                              borderRadius: 14,
                              border: '1px solid var(--stroke)',
                              borderLeft: `4px solid ${row.late > 0 ? 'var(--red)' : 'var(--green)'}`,
                              background: 'var(--panel2)',
                            }}
                          >
                            <Av $sm $bg={avColor(idx)}>{initials(row.name)}</Av>
                            <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                              <div style={{ fontWeight: 800, fontSize: 13.5 }}>{row.name}</div>
                              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--mut)', marginTop: 2 }}>
                                {row.done} задач · в срок {row.onTimePct != null ? `${row.onTimePct}%` : '—'}
                                {row.late ? ` · опозданий ${row.late}` : ''}
                              </div>
                              {!!(row.topDelayReasons || []).length && (
                                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--amber)', marginTop: 4 }}>
                                  {(row.topDelayReasons || []).map((x) => `${x.label} (${x.count})`).join(' · ')}
                                </div>
                              )}
                            </div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 800, color: 'var(--mut)', textAlign: 'right' }}>
                              план {row.avgPlanMin ?? '—'} · факт {row.avgActualMin ?? '—'}
                              {row.planActualRatio != null ? (
                                <div style={{ color: row.planActualRatio > 1 ? 'var(--red)' : 'var(--green)' }}>
                                  факт/план {row.planActualRatio}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)', letterSpacing: '.4px', textTransform: 'uppercase', marginBottom: 8 }}>
                      Частые причины опозданий
                    </div>
                    {!(analyticsData.delayReasons || []).length ? (
                      <div style={{ color: 'var(--mut)', fontWeight: 700 }}>Пока нет опозданий с указанной причиной</div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {analyticsData.delayReasons.map((r) => {
                          const max = analyticsData.delayReasons[0]?.count || 1;
                          const pct = Math.max(8, Math.round((r.count / max) * 100));
                          return (
                            <div key={r.reason}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, fontWeight: 700, marginBottom: 4 }}>
                                <span>{r.label}</span>
                                <span style={{ fontFamily: "'JetBrains Mono', monospace", color: 'var(--amber)' }}>{r.count}</span>
                              </div>
                              <div style={{ height: 8, borderRadius: 99, background: 'var(--panel)', overflow: 'hidden', border: '1px solid var(--stroke)' }}>
                                <div style={{ width: `${pct}%`, height: '100%', background: 'linear-gradient(90deg, #fbbf24, #f59e0b)', borderRadius: 99 }} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </FilterBody>
              <FilterNav>
                <div style={{ fontSize: 11, fontWeight: 800, color: 'var(--mut)' }}>
                  Без ярлыков — только факты для разбора с сотрудником
                </div>
                <NavBtn type="button" onClick={() => setAnalyticsOpen(false)}>Закрыть</NavBtn>
              </FilterNav>
            </FilterShell>
          </ModalBack>
        )}

        {donePrompt && (
          <ModalBack className="kanban-modal-ui" onClick={closeDonePrompt}>
            <MBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Задача вышла за лимит</h3>
              <div style={{ color: 'var(--mut)', fontWeight: 700, fontSize: 12.5, marginBottom: 14, lineHeight: 1.45 }}>
                «{donePrompt.task?.title}»
                <br />
                План: {donePrompt.plan ?? '—'} мин · Факт ≈ {donePrompt.actual ?? '—'} мин
                {donePrompt.plan != null && donePrompt.actual != null
                  ? ` (${formatTimingDelta(donePrompt.plan, donePrompt.actual)})`
                  : ''}
                <br />
                Почему не уложились?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                {DELAY_REASONS.map((r) => (
                  <label
                    key={r.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 12px',
                      borderRadius: 12,
                      border: `1px solid ${delayReason === r.id ? 'var(--amber)' : 'var(--stroke)'}`,
                      background: delayReason === r.id ? 'rgba(251,191,36,.12)' : 'var(--panel2)',
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="delayReason"
                      value={r.id}
                      checked={delayReason === r.id}
                      onChange={() => setDelayReason(r.id)}
                      style={{ accentColor: '#fbbf24' }}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              {delayReason === 'other' && (
                <textarea
                  value={delayNote}
                  onChange={(e) => setDelayNote(e.target.value)}
                  placeholder="Краткий комментарий…"
                  rows={3}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    marginBottom: 12,
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '1px solid var(--stroke)',
                    background: 'var(--panel2)',
                    color: 'var(--txt)',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    fontSize: 13,
                    resize: 'vertical',
                  }}
                />
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <ActBtn $kind="ret" type="button" onClick={closeDonePrompt} disabled={doneSubmitting}>
                  Отмена
                </ActBtn>
                <ActBtn $kind="toDone" type="button" onClick={submitDoneWithReason} disabled={doneSubmitting}>
                  {doneSubmitting ? 'Отправка…' : 'Отправить в готово'}
                </ActBtn>
              </div>
            </MBox>
          </ModalBack>
        )}

        {boardForm && (
          <ModalBack className="kanban-modal-ui" onClick={() => !boardSaving && setBoardForm(null)}>
            <MBox onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
              <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>
                {boardForm.mode === 'create' ? 'Новая доска' : 'Название доски'}
              </h3>
              <div style={{ color: 'var(--mut)', fontWeight: 700, fontSize: 12.5, marginBottom: 14, lineHeight: 1.45 }}>
                {boardForm.mode === 'create'
                  ? 'Например: Отдел кадров, Колл-центр, Логистика. Вы станете руководителем этой доски.'
                  : 'Как называется отдел, задачи которого ведутся на этой доске.'}
              </div>
              <input
                autoFocus
                value={boardForm.name}
                onChange={(e) => setBoardForm((f) => ({ ...f, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') submitBoardForm(); }}
                placeholder="Название отдела"
                maxLength={80}
                style={{
                  width: '100%',
                  boxSizing: 'border-box',
                  marginBottom: 14,
                  padding: '11px 13px',
                  borderRadius: 12,
                  border: '1px solid var(--stroke)',
                  background: 'var(--panel2)',
                  color: 'var(--txt)',
                  fontFamily: 'inherit',
                  fontWeight: 700,
                  fontSize: 14,
                }}
              />
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <ActBtn $kind="ret" type="button" onClick={() => setBoardForm(null)} disabled={boardSaving}>
                  Отмена
                </ActBtn>
                <ActBtn $kind="toDone" type="button" onClick={submitBoardForm} disabled={boardSaving}>
                  {boardSaving ? 'Сохранение…' : boardForm.mode === 'create' ? 'Создать' : 'Сохранить'}
                </ActBtn>
              </div>
            </MBox>
          </ModalBack>
        )}
      </Shell>
    </Overlay>
  );
}
